import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Download, Upload, FileSpreadsheet, Search, ExternalLink, Repeat, Filter, LayoutList, Table2, AlertTriangle, Mail, History, RefreshCw, Layers, Printer, Pencil, Columns, GripVertical, ArrowUpDown, SlidersHorizontal, MoreHorizontal } from "lucide-react";
import MultiSelectFilter from "@/components/MultiSelectFilter";
import MoreActionsMenu from "@/components/MoreActionsMenu";
import SortMenu from "@/components/SortMenu";
import { PageHeader, PageBody, StatusPill, statusTone } from "@/components/PageChrome";
import { Button } from "@/components/controls";
import PaginationBar from "@/components/PaginationBar";
import { T } from "@/lib/testIds";
import { usePerms, useAuth } from "@/lib/auth";
import FieldFilterBar, { applyFieldFilters } from "@/components/FieldFilterBar";
import FilterPresets from "@/components/FilterPresets";
import ImageThumb from "@/components/ImageThumb";
import { TranslateInline } from "@/components/TranslateInline";

// Feb 27 2026 — canonical date formatter for the Orders grid + detail views.
// Renders any ISO / dd-mm-yyyy / mm/dd/yyyy / DD-Mon-YY input as `DD-Mon-YYYY`
// (e.g. `01-Apr-2026`). Returns "—" for null/blank, or the raw string when
// the value can't be parsed so ops always sees SOMETHING.
const _MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                 "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function formatDMY(v) {
  if (v === null || v === undefined || v === "") return "—";
  const s = String(v).trim();
  if (!s) return "—";
  // ISO or ISO-with-time → take the date portion.
  let match = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const y = Number(match[1]), m = Number(match[2]), d = Number(match[3]);
    if (m >= 1 && m <= 12) return `${String(d).padStart(2, "0")}-${_MONTHS[m - 1]}-${y}`;
  }
  // dd/mm/yyyy or dd-mm-yyyy (also accept 2-digit year)
  match = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (match) {
    let y = Number(match[3]);
    const d = Number(match[1]), m = Number(match[2]);
    if (y < 100) y += 2000;
    if (m >= 1 && m <= 12) return `${String(d).padStart(2, "0")}-${_MONTHS[m - 1]}-${y}`;
  }
  // Amazon short (DD-Mon-YY) — normalise year to 4 digits.
  match = s.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
  if (match) {
    const monIdx = _MONTHS.findIndex((mm) => mm.toLowerCase() === match[2].toLowerCase());
    if (monIdx >= 0) {
      let y = Number(match[3]);
      if (y < 100) y += 2000;
      return `${String(Number(match[1])).padStart(2, "0")}-${_MONTHS[monIdx]}-${y}`;
    }
  }
  // Feb 27 2026 — Flipkart "Aug 02, 2026" / "Aug 2, 2026" (also without the comma).
  match = s.match(/^([A-Za-z]{3,})\s+(\d{1,2}),?\s+(\d{4})/);
  if (match) {
    const monIdx = _MONTHS.findIndex((mm) => mm.toLowerCase() === match[1].slice(0, 3).toLowerCase());
    if (monIdx >= 0) {
      return `${String(Number(match[2])).padStart(2, "0")}-${_MONTHS[monIdx]}-${Number(match[3])}`;
    }
  }
  return s;
}

// "Compact" mode column allowlist — the trader's day-to-day lifecycle view.
// Anything not in this set is hidden when density === "compact".
const COMPACT_COLS = new Set([
  "image", "created_date", "account_id", "marketplace_identifier",
  "system_sku", "product_name", "qty_purchased", "order_id", "item_price",
  "final_transfer_price", "importrend_status", "marketplace_status",
  "china_order_status", "shipping_status", "china_id",
  "system_id", "marketplace", "customer", "working_status",
  "refund_status", "return_received_status",
  // Finance columns kept in compact view — these are the primary Gmail-reader
  // outputs (payout = "Your earnings", commission = "Amazon fees") and the
  // user needs to see them without switching to full density.
  "amazon_payout", "amazon_commission",
]);

// Filter field registry — client-side dynamic filters gated by canView("orders", key)
// `opts` receives the merged dropdowns map. We inject { ...serverDropdowns, account_id: [...] }
// so account_id and similar reference-table fields render as a select.
const ORDER_FILTER_FIELDS = [
  { key: "system_id", label: "System ID", type: "text" },
  { key: "marketplace", label: "Marketplace", type: "select", opts: (d) => d.marketplace || [] },
  { key: "account_id", label: "Account", type: "select", opts: (d) => d.account_id || [] },
  { key: "marketplace_identifier", label: "Marketplace ID", type: "text" },
  { key: "system_sku", label: "System SKU", type: "text" },
  { key: "product_name", label: "Product Name", type: "text" },
  { key: "product_type", label: "Product Type", type: "select", opts: (d) => d.product_type || [] },
  { key: "product_condition", label: "Product Condition", type: "select", opts: (d) => d.product_condition || [] },
  { key: "working_status", label: "Working Status", type: "select", opts: (d) => d.working_status || [] },
  { key: "marketplace_status", label: "MP Status", type: "select", opts: (d) => d.marketplace_status || [] },
  { key: "shipping_status", label: "Shipping Status", type: "select", opts: (d) => d.shipping_status || [] },
  { key: "china_order_status", label: "China Status", type: "select", opts: (d) => d.china_order_status || [] },
  // China No / China ID — numeric picker sourced from `/orders/distinct/china_id`.
  // The backend returns raw numeric values so the multi-select `$in` payload
  // matches int-typed docs. Includes the standard blank/empty toggle.
  { key: "china_id", label: "China No", type: "select" },
  { key: "refund_status", label: "Refund Status", type: "select", opts: (d) => d.refund_status || [] },
  { key: "claim_status", label: "Claim Status", type: "select", opts: (d) => d.claim_status || [] },
  { key: "importrend_status", label: "Importrend Status", type: "select", opts: (d) => d.importrend_status || [] },
  { key: "return_received_status", label: "Return Status", type: "select", opts: (d) => d.return_received_status || [] },
  { key: "price_updated_status", label: "Price Updated Status", type: "select", opts: (d) => d.price_updated_status || [] },
  { key: "electrical_type", label: "Electrical Type", type: "select", opts: (d) => d.electrical_type || [] },
  { key: "payment_method", label: "Payment Method", type: "select", opts: (d) => d.payment_method || ["COD", "Prepaid"] },
  { key: "purchase_date", label: "Purchase Date", type: "date" },
  { key: "shipping_date", label: "Shipping Date", type: "date" },
  { key: "china_order_date", label: "China Order Date", type: "date" },
  { key: "china_order_received_date", label: "China Received Date", type: "date" },
  { key: "china_order_dispatched_date", label: "China Dispatched Date", type: "date" },
  { key: "item_price", label: "Item Price ₹", type: "number" },
  { key: "qty_purchased", label: "Qty", type: "qty" },
  { key: "china_transfer_price", label: "Transfer ₹", type: "number" },
  { key: "calculated_sp", label: "SP ₹", type: "number" },
  { key: "amazon_commission", label: "Amazon Commission ₹", type: "number" },
  { key: "amazon_payout", label: "Amazon Payout ₹", type: "number" },
  { key: "importrend_margin", label: "Importrend Margin ₹", type: "number" },
  // Sep 20 2026 — convert Buy Back and China Qty to multi-select
  // dropdowns (with search + blank/empty toggle) so ops can filter to
  // orders that carry a specific value OR were left unset. Distinct
  // values come from `/orders/distinct/<field>` — see backend
  // `_DISTINCT_ALLOWED_FIELDS` / `_DISTINCT_NUMERIC_FIELDS`.
  { key: "buy_back_price", label: "Buy Back ₹", type: "select" },
  { key: "china_qty_to_order", label: "China Qty", type: "select" },
  // Tax section
  { key: "tax_description", label: "Tax Description", type: "text" },
  { key: "hsn", label: "HSN", type: "text" },
  { key: "tax_rate", label: "Tax Rate %", type: "number" },
  { key: "chargeable_amount", label: "Chargeable ₹", type: "number" },
  { key: "tax_amount", label: "Tax Amount ₹", type: "number" },
  { key: "qty", label: "Tax Qty", type: "qty" },
  { key: "total_amount", label: "Total ₹", type: "number" },
  { key: "invoice_by_company", label: "Invoice By Company", type: "text" },
  { key: "recipient_name", label: "Recipient", type: "text" },
  { key: "ship_city", label: "Ship City", type: "text" },
  { key: "ship_state", label: "Ship State", type: "text" },
  { key: "ship_postal_code", label: "Pincode", type: "text" },
  { key: "buyer_phone_no", label: "Buyer Phone", type: "text" },
  { key: "buyer_email", label: "Buyer Email", type: "text" },
  { key: "tags", label: "Tags", type: "text" },
  { key: "courier_name", label: "Courier", type: "text" },
  { key: "tracking_id", label: "Tracking ID", type: "text" },
  // Sep 20 2026 — convert China Shipment ID filter to multi-select
  // (search + blank/empty). Distinct values come from
  // `/orders/distinct/china_shipment_id`.
  { key: "china_shipment_id", label: "China Shipment ID", type: "select" },
  // Comments: multi-select with blank support. Options populated from the
  // distinct-values endpoint (see `commentOptions` state) so ops can pick
  // real free-text notes without typing. Falls through to `dropdowns.comments`
  // via the FieldFilterBar's opts resolution.
  { key: "comments", label: "Comments", type: "select" },
  // URL fields (Feb 2026 spec — filterable across all modules)
  { key: "product_url", label: "Product URL", type: "text" },
  { key: "image_url", label: "Image URL", type: "text" },
  { key: "source_url", label: "Source URL", type: "text" },
  { key: "china_image_url", label: "China Image URL", type: "text" },
];

/**
 * ColumnsPanel — right-hand slide-over that lets the user pick which order
 * columns to show and drag them into any order. Persists to
 * /api/auth/preferences/orders_columns via the parent's onSave.
 * Feb 27 2026.
 */
function ColumnsPanel({ allColumns, initial, onClose, onSave, onReset }) {
  const savedOrder = initial?.columns || [];
  const savedHidden = new Set(initial?.hidden || []);
  // Merge: saved order first, then any new columns not yet ranked.
  const mergedOrder = React.useMemo(() => {
    const map = Object.fromEntries(allColumns.map((c) => [c.k, c]));
    const seen = new Set();
    const out = [];
    for (const k of savedOrder) { if (map[k] && !seen.has(k)) { out.push(map[k]); seen.add(k); } }
    for (const c of allColumns) { if (!seen.has(c.k)) { out.push(c); seen.add(c.k); } }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [order, setOrder] = React.useState(mergedOrder);
  const [hidden, setHidden] = React.useState(savedHidden);
  const [dragKey, setDragKey] = React.useState(null);
  const toggle = (k) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const onDragStart = (k) => (e) => { setDragKey(k); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (k) => (e) => {
    e.preventDefault();
    if (!dragKey || dragKey === k) return;
    setOrder((prev) => {
      const src = prev.findIndex((c) => c.k === dragKey);
      const dst = prev.findIndex((c) => c.k === k);
      if (src < 0 || dst < 0) return prev;
      const next = prev.slice();
      const [item] = next.splice(src, 1);
      next.splice(dst, 0, item);
      return next;
    });
  };
  const onDrop = () => setDragKey(null);
  const save = () => onSave({
    columns: order.map((c) => c.k),
    hidden: [...hidden],
  });
  // Group columns for the render
  const groups = React.useMemo(() => {
    const g = new Map();
    for (const c of order) {
      const k = c.group || "Other";
      if (!g.has(k)) g.set(k, []);
      g.get(k).push(c);
    }
    return [...g.entries()];
  }, [order]);
  const visibleCount = order.length - hidden.size;
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="orders-columns-panel">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-[420px] bg-white shadow-2xl h-full flex flex-col border-l border-slate-200">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-heading text-[14px] font-bold text-slate-900">Columns</div>
            <div className="text-[11px] text-slate-500">{visibleCount} visible · drag to reorder</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="orders-columns-close">✕</button>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-2 text-[12.5px]">
          {groups.map(([g, cols]) => (
            <div key={g} className="mb-3">
              <div className="uppercase text-[10.5px] tracking-wider text-slate-500 font-bold px-1 mb-1">{g}</div>
              <div className="border border-slate-200 rounded-sm bg-white">
                {cols.map((c) => (
                  <div
                    key={c.k}
                    draggable
                    onDragStart={onDragStart(c.k)}
                    onDragOver={onDragOver(c.k)}
                    onDrop={onDrop}
                    data-testid={`orders-columns-row-${c.k}`}
                    className={`flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 last:border-0 cursor-move ${dragKey === c.k ? "bg-blue-50" : "hover:bg-slate-50"}`}
                  >
                    <GripVertical size={12} className="text-slate-400 shrink-0" />
                    <input
                      type="checkbox"
                      checked={!hidden.has(c.k)}
                      onChange={() => toggle(c.k)}
                      data-testid={`orders-columns-check-${c.k}`}
                      className="cursor-pointer shrink-0"
                    />
                    <span className="truncate">{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 px-4 py-3 flex items-center justify-between bg-slate-50">
          <button onClick={onReset} className="text-[12px] text-slate-600 hover:text-red-600" data-testid="orders-columns-reset">Reset to default</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="h-8 px-3 text-[12px] font-semibold rounded-sm bg-white border border-slate-200 hover:bg-slate-100">Cancel</button>
            <button onClick={save} data-testid="orders-columns-save" className="h-8 px-3 text-[12px] font-bold uppercase tracking-wider rounded-sm bg-slate-900 text-white hover:bg-slate-800">Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * InlineEditCell — hover to reveal a pencil, click to edit in place.
 * Save = PATCH via `onSave`. Cancel = restore & no API call.
 * Feb 27 2026. Keeps the existing per-field audit log path via /orders PATCH.
 */
function InlineEditCell({ value, spec, onSave, className = "", display, testId }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  const start = (e) => {
    e.stopPropagation();
    // Coerce the current value into an input-friendly string.
    let v = value;
    if (spec.type === "date" && typeof v === "string" && v.length >= 10) v = v.slice(0, 10);
    setDraft(v ?? "");
    setEditing(true);
  };
  const save = async () => {
    if (String(draft ?? "") !== String(value ?? "")) {
      await onSave(draft);
    }
    setEditing(false);
  };
  const cancel = () => { setEditing(false); setDraft(""); };
  if (editing) {
    const stopBubble = (e) => e.stopPropagation();
    return (
      <td className={className} onClick={stopBubble}>
        {spec.type === "select" ? (
          <select
            autoFocus
            data-testid={testId || undefined}
            value={draft ?? ""}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
            className="h-6 text-[11.5px] border border-blue-400 rounded-sm px-1 bg-white w-full max-w-[160px]"
          >
            <option value=""></option>
            {(spec.options || []).map((opt) => (
              <option key={typeof opt === "string" ? opt : opt.value} value={typeof opt === "string" ? opt : opt.value}>
                {typeof opt === "string" ? opt : (opt.label || opt.value)}
              </option>
            ))}
          </select>
        ) : (
          <input
            autoFocus
            data-testid={testId || undefined}
            type={spec.type === "number" ? "number" : (spec.type === "date" ? "date" : "text")}
            value={draft ?? ""}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={save}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
            className="h-6 text-[11.5px] border border-blue-400 rounded-sm px-1 bg-white w-full max-w-[140px]"
          />
        )}
      </td>
    );
  }
  return (
    <td className={`${className} group relative`}>
      <span className="inline-flex items-center gap-1">
        {display !== undefined ? display : (value ?? "—")}
        <button
          type="button"
          onClick={start}
          className="opacity-0 group-hover:opacity-100 transition-opacity ml-1 text-slate-400 hover:text-blue-600"
          title="Edit inline"
          data-testid={testId ? `${testId}-open` : undefined}
        >
          <Pencil size={10} />
        </button>
      </span>
    </td>
  );
}

export default function Orders() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  // Mar 2 2026 — default view shows ONLY orders with a blank marketplace_status
  // (i.e. still-open orders that haven't been Shipped / Cancelled / Returned).
  // The backend's ``{empty: true}`` matches null / missing / "" / [] for any
  // field. This default combines with any other filter the user applies; if
  // the user explicitly picks a marketplace_status value from the filter UI,
  // that value replaces the ``{empty: true}`` default in place.
  const [filter, setFilter] = useState({ marketplace_status: { empty: true } });
  const [loading, setLoading] = useState(false);
  const [dropdowns, setDropdowns] = useState({});
  const [accounts, setAccounts] = useState([]);
  // Mar 1 2026 — top-bar Warehouse filter needs its own option list. Sourced
  // from `/warehouse` (canonical warehouse table) so the dropdown shows every
  // warehouse defined in the system, even ones with zero orders yet.
  const [warehouses, setWarehouses] = useState([]);
  const [extraFilterFields, setExtraFilterFields] = useState([]);
  // Feb 28 2026 — context-aware filter option lists. Keyed by field name;
  // each value is the distinct set of options remaining under the CURRENT
  // filter set (self-excluded so the user can still switch values on the
  // field they're editing). Merged into `filterDropdowns` at render time —
  // when a field has a context list here it overrides the static one.
  const [contextOptions, setContextOptions] = useState({});
  const [showFilterBar, setShowFilterBar] = useState(false);
  const [density, setDensity] = useState(() => localStorage.getItem("orders_density") || "compact");
  const setDensityPersist = (d) => { setDensity(d); localStorage.setItem("orders_density", d); };
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => Number(localStorage.getItem("orders_page_size")) || 50);
  const [total, setTotal] = useState(0);
  const importFileRef = useRef(null);
  // Feb 27 2026 — selected order ids for the bulk-delete flow.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDeleteSelected, setConfirmDeleteSelected] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState("");
  const [deletingSelected, setDeletingSelected] = useState(false);
  const navigate = useNavigate();
  const { canView, canBulkEdit, allowedWarehouses } = usePerms();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [showWipe, setShowWipe] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");
  const [wiping, setWiping] = useState(false);
  // Backfill modal state — Feb 21 2026. Dry-run defaults ON so ops see the
  // impact before committing, and the result summary is shown below the
  // buttons after the request returns.
  const [showBackfill, setShowBackfill] = useState(false);
  const [backfillDryRun, setBackfillDryRun] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  // Feb 27 2026 — sort dropdown (server-side; defaults blank so existing
  // system_id-descending sort stays intact when no explicit choice is made).
  const [sortKey, setSortKey] = useState(() => localStorage.getItem("orders_sort_key") || "");
  // Feb 27 2026 — per-user column preferences. `null` = never loaded /
  // fall back to defaults, `{}` empty object = loaded but user has no saved
  // pref, otherwise `{columns: ["k1","k2",...], hidden: ["k3"]}`.
  const [columnPref, setColumnPref] = useState(null);
  const [showColumnPanel, setShowColumnPanel] = useState(false);
  // Feb 27 2026 — Bulk Update bar (permission-gated). `bulkField` is the
  // target column, `bulkValue` is the new value, `bulkMode` toggles between
  // "all" (every filtered row) and "blanks" (only rows whose target field
  // is blank/null/0).
  const [bulkField, setBulkField] = useState("");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkMode, setBulkMode] = useState("all");  // "all" | "blanks"
  const [bulkConfirm, setBulkConfirm] = useState(null);  // { count, ids }
  const [bulkRunning, setBulkRunning] = useState(false);
  const [syncingAllocations, setSyncingAllocations] = useState(false);

  // Feb 25 2026 — bulk reclassify china_order_status. Same UX shape as
  // Backfill Allocations: confirm modal + dry-run toggle + result panel.
  const [showReclassify, setShowReclassify] = useState(false);
  const [reclassifyDryRun, setReclassifyDryRun] = useState(true);
  const [reclassifying, setReclassifying] = useState(false);
  const [reclassifyResult, setReclassifyResult] = useState(null);

  const runReclassify = async () => {
    setReclassifying(true);
    try {
      const { data } = await api.post("/orders/admin/reclassify", {
        confirm: "RECLASSIFY CHINA STATUS",
        dry_run: reclassifyDryRun,
      });
      setReclassifyResult(data);
      if (!reclassifyDryRun) {
        toast.success(`Reclassified: ${data.flipped_to_eligible} → Eligible, ${data.flipped_to_price_check} → Price-Check`);
        load();
      } else {
        toast.message(`Dry-run: ${data.flipped_to_eligible} would flip → Eligible, ${data.flipped_to_price_check} → Price-Check.`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setReclassifying(false);
  };

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data } = await api.post("/orders/admin/backfill-allocations", {
        confirm: "BACKFILL ALLOCATIONS",
        dry_run: backfillDryRun,
      });
      setBackfillResult(data);
      if (!backfillDryRun) {
        toast.success(`Allocated ${data.allocated} of ${data.considered} orders`);
        load();
      } else {
        toast.message(`Dry-run: ${data.allocated} orders would be allocated (${data.skipped_no_inventory} skipped for missing stock).`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setBackfilling(false);
  };

  const handleImportFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", f);
      // Default: skip duplicate order_item_ids to avoid overwriting existing
      // orders. Pass `update_existing=true` explicitly if you want to overwrite.
      const { data } = await api.post("/imports/orders", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // The import runs as a background job, so this POST only returns
      // {queued, job_id, total} - it carries no counts. Reading created /
      // updated / skipped straight off it is why the popup always said
      // "0 updates". The real counts land on the job, so poll for them, the
      // same way the Imports and Catalog pages already do.
      if (data.job_id) {
        const finished = await new Promise((resolve) => {
          let tries = 0;
          let last = null;
          const pollId = setInterval(async () => {
            tries += 1;
            try {
              const { data: job } = await api.get(`/imports/status/${data.job_id}`);
              last = job;
              if (job.status !== "running") {
                clearInterval(pollId);
                resolve(job);
                return;
              }
            } catch { /* transient - keep polling */ }
            // Never poll forever. If the worker dies without reporting, give up
            // after ~5 minutes and show whatever the job last reported rather
            // than leaving the page stuck on "Importing".
            if (tries >= 250) {
              clearInterval(pollId);
              resolve(last || { status: "done", result: {} });
            }
          }, 1200);
        });
        if (finished.status === "failed") {
          toast.error(`Import failed: ${finished.error || "unknown error"}`);
        } else {
          const r = finished.result || finished;
          setImportResult({
            ...finished,
            created: r.created ?? 0,
            updated: r.updated ?? 0,
            skipped: r.skipped ?? 0,
            filename: f.name,
          });
          toast.success(`Imported ${r.created ?? 0} new, ${r.updated ?? 0} updated, ${r.skipped ?? 0} skipped`);
        }
      } else {
        // Legacy synchronous response - counts are already on the body.
        setImportResult({ ...data, filename: f.name });
        toast.success(`Imported ${data.created ?? 0} new, ${data.updated ?? 0} updated, ${data.skipped ?? 0} skipped`);
      }
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setImporting(false);
    e.target.value = ""; // allow re-selecting the same file
  };

  const downloadTemplate = () => downloadFile("/downloads/templates/orders.csv", "orders_template.csv");

  // Feb 25 2026 — bulk labels: POST current filter payload, backend
  // streams a single multi-page 100x150mm PDF, we open it in a new tab
  // so ops can review + print. Uses fetch (not axios) so we get the raw
  // Response and can convert to a Blob.
  const bulkPrintLabels = async () => {
    try {
      const body = {
        filter_json: filter || {},
        filter_types: filterTypesMap || {},
        limit: 500,
      };
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/labels/bulk`;
      const token = localStorage.getItem("access_token");
      const resp = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const detail = (await resp.json().catch(() => ({}))).detail || `HTTP ${resp.status}`;
        toast.error(detail);
        return;
      }
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank", "noopener");
      // Revoke after 60s so the browser can still stream the print dialog.
      setTimeout(() => URL.revokeObjectURL(objUrl), 60000);
    } catch (e) { toast.error(e.message || "Bulk label generation failed"); }
  };

  useEffect(() => {
    api.get("/settings/dropdowns").then((r) => setDropdowns(r.data)).catch(() => {});
    api.get("/accounts").then((r) => setAccounts(r.data || [])).catch(() => {});
    api.get("/warehouse").then((r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setWarehouses(items);
    }).catch(() => {});
    // Pull distinct comment values so the "Comments" filter can render as a
    // curated multi-select. Merged into the same `dropdowns` map that
    // FieldFilterBar reads from — so no extra plumbing needed on the filter
    // side. Wrapped in its own .then so a failure here doesn't drop the
    // static dropdowns above.
    api.get("/orders/distinct/comments").then((r) => {
      setDropdowns((d) => ({ ...(d || {}), comments: r.data?.values || [] }));
    }).catch(() => {});
    // China No — numeric multi-select picker. Backend returns raw ints; keep
    // them numeric-typed here so the FieldFilterBar's `$in` matches Mongo.
    // Sep 7 2026 — pull with a much higher cap (5000) so mid-6-figure
    // china_id values aren't silently dropped from the initial dropdown.
    // The FieldFilterBar's popover search box also falls back to a
    // server-side ``q=`` query for anything not in this initial slice.
    api.get("/orders/distinct/china_id", { params: { limit: 5000 } }).then((r) => {
      setDropdowns((d) => ({ ...(d || {}), china_id: r.data?.values || [] }));
    }).catch(() => {});
    // Sep 20 2026 — Buy Back, China Qty, China Shipment ID converted to
    // multi-select filters. Pull their distinct values so the popover
    // shows real options; the "(blank / empty)" toggle is handled by
    // FieldFilterBar's shared select branch and doesn't require a
    // separate fetch. Each request is wrapped so one failing endpoint
    // (permissions / new field) doesn't drop the others.
    api.get("/orders/distinct/buy_back_price", { params: { limit: 5000 } }).then((r) => {
      setDropdowns((d) => ({ ...(d || {}), buy_back_price: r.data?.values || [] }));
    }).catch(() => {});
    api.get("/orders/distinct/china_qty_to_order", { params: { limit: 5000 } }).then((r) => {
      setDropdowns((d) => ({ ...(d || {}), china_qty_to_order: r.data?.values || [] }));
    }).catch(() => {});
    api.get("/orders/distinct/china_shipment_id", { params: { limit: 5000 } }).then((r) => {
      setDropdowns((d) => ({ ...(d || {}), china_shipment_id: r.data?.values || [] }));
    }).catch(() => {});
    // Pull the full order schema so every MODULE_FIELDS entry becomes filterable
    // — anything not already hard-coded above (better labels/opts) is appended
    // with the backend-inferred type. Number-inferred fields are widened to
    // `number-range` so ops get min/max inputs.
    api.get("/settings/schema/orders").then((r) => {
      const hardcodedKeys = new Set(ORDER_FILTER_FIELDS.map((f) => f.key));
      const extras = (r.data.fields || [])
        .filter((f) => !hardcodedKeys.has(f.key))
        .map((f) => ({
          key: f.key,
          label: f.label,
          type: f.type === "number" ? "number" : f.type,  // number-range would be nicer but keep simple
          ...(f.type === "select" && Array.isArray(f.options) ? { opts: f.options } : {}),
        }));
      setExtraFilterFields(extras);
    }).catch(() => {});
  }, []);

  // Gmail sync freshness badge next to the Sync From Gmail button (Feb 25 2026).
  // Auto-poller was disabled — this badge is the ops team's only visual hint
  // that the last sync is stale before an EOD reconciliation. We poll every
  // 60s (cheap Mongo read) and re-fetch immediately after clicking Sync.
  const [gmailLastRun, setGmailLastRun] = useState(null);
  const [gmailLastStatus, setGmailLastStatus] = useState(null);
  const refreshGmailStatus = async () => {
    try {
      const { data } = await api.get("/gmail/status");
      // Prefer `ran_at` (completion time) but fall back to `started_at` for
      // legacy docs that predate the field so the badge still shows.
      setGmailLastRun(data?.ran_at || data?.started_at || null);
      setGmailLastStatus(data?.status || null);
    } catch {
      // Non-admins hit 403 here; swallow so they don't see error toasts.
    }
  };
  useEffect(() => {
    if (!isAdmin) return;
    refreshGmailStatus();
    const t = setInterval(refreshGmailStatus, 60_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);
  // Force the "X min ago" label to re-render every 30s so the count stays live
  // without re-fetching the status endpoint.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  const gmailAgeLabel = useMemo(() => {
    if (!gmailLastRun) return null;
    const ms = Date.now() - new Date(gmailLastRun).getTime();
    if (Number.isNaN(ms) || ms < 0) return null;
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gmailLastRun, tick]);
  const gmailAgeTone = useMemo(() => {
    if (!gmailLastRun) return "slate";
    const min = (Date.now() - new Date(gmailLastRun).getTime()) / 60_000;
    if (Number.isNaN(min) || min < 0) return "slate";
    if (min < 30) return "emerald";      // fresh
    if (min < 240) return "amber";       // stale (30 min – 4h)
    return "red";                         // very stale (>4h)
  }, [gmailLastRun, tick]);

  // Merged filter field list — hardcoded fields keep their curated labels /
  // select opts, then every remaining MODULE_FIELDS entry (China ID, Final
  // Transfer Price, all the China-costing computed fields, bill addresses,
  // marketplace details, etc.) is appended dynamically so ops can filter by
  // any field the module registers.
  const filterFields = useMemo(
    () => [...ORDER_FILTER_FIELDS, ...extraFilterFields],
    [extraFilterFields]
  );

  // Merge the reference-table lists (accounts, etc.) into the dropdowns map so
  // FieldFilterBar renders account_id as a select.
  // Feb 28 2026 — context-aware overrides win. Any field present in
  // `contextOptions` (populated from the server against the current filter
  // set, minus itself) overrides the static list so downstream dropdowns
  // only show values that co-exist with the caller's other filters.
  const filterDropdowns = useMemo(() => {
    const base = {
      ...dropdowns,
      account_id: [...new Set(accounts.map((a) => a.account_id).filter(Boolean))].sort(),
    };
    for (const [k, v] of Object.entries(contextOptions || {})) {
      if (Array.isArray(v) && v.length >= 0) base[k] = v;
    }
    return base;
  }, [dropdowns, accounts, contextOptions]);

  // A "rich" client filter is anything the backend needs help with —
  // object-shape values ({min/max}, {from/to}, {in:[...]}, {empty:true}).
  // These are now pushed server-side via filter_json (Feb 21 2026) instead
  // of fetching 50k rows and filtering in the browser.
  const hasClientSideFilter = useMemo(
    () => Object.values(filter).some((v) => v && typeof v === "object"),
    [filter]
  );

  // Build a { key: type } map from filterFields so the backend knows how to
  // interpret each shape. Recomputed only when the schema arrives.
  const filterTypesMap = useMemo(() => {
    const m = {};
    for (const f of filterFields) m[f.key] = f.type || "text";
    // Mar 1 2026 — top-bar Warehouse filter uses `warehouse_name` which isn't
    // an orders-schema field (resolved server-side via accounts). Force the
    // type so filter_json → backend translation treats it as a select.
    m.warehouse_name = "select";
    return m;
  }, [filterFields]);

  // Feb 28 2026 — context-aware filter dropdowns. Whenever the active filter
  // set changes, re-fetch distinct values for every filter field from the
  // server so downstream dropdowns show only options that co-exist with the
  // filters already applied. Debounced (250ms) so rapid clicks don't spam
  // the API. When the filter set is empty we clear the overrides and the
  // static `dropdowns` map takes over again.
  //
  // Self-exclusion: for each fetched field ``F`` we exclude ``filter[F]`` from
  // the query so the popover for F still shows every value F can take (only
  // filters on OTHER fields narrow the F options). This matches the user's
  // spec: "if Marketplace = Flipkart is applied and the user opens the
  // Account filter, only show accounts that have at least one Flipkart order"
  // (Account is narrowed by Marketplace, not by its own current selection).
  const CONTEXT_AWARE_FIELDS = useMemo(() => [
    "marketplace", "account_id", "china_order_status", "working_status",
    "marketplace_status", "return_received_status", "refund_status",
    "product_condition", "electrical_type", "importrend_status",
    "courier_name", "invoice_by_company", "product_type", "tags",
    "tax_description", "hsn", "comments", "china_id",
    "shipping_status", "price_updated_status", "claim_status",
  ], []);

  useEffect(() => {
    // No active filter → clear overrides so static dropdowns take over.
    const activeKeys = Object.keys(filter || {}).filter((k) => {
      const v = filter[k];
      if (v === null || v === undefined || v === "") return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    });
    if (activeKeys.length === 0) {
      setContextOptions({});
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      // Fetch every context-aware field in parallel. Each request self-
      // excludes ``field`` server-side so the popover for that field can
      // still show its full domain (only OTHER filters narrow its list).
      const results = await Promise.allSettled(
        CONTEXT_AWARE_FIELDS.map(async (fieldKey) => {
          const params = {
            filter_json: JSON.stringify(filter),
            filter_types_json: JSON.stringify(filterTypesMap),
          };
          const { data } = await api.get(`/orders/distinct/${fieldKey}`, {
            params, signal: controller.signal,
          });
          return [fieldKey, data?.values || []];
        })
      );
      const next = {};
      for (const r of results) {
        if (r.status === "fulfilled") {
          const [k, v] = r.value;
          next[k] = v;
        }
      }
      setContextOptions(next);
    }, 250);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [filter, filterTypesMap, CONTEXT_AWARE_FIELDS]);


  // Feb 28 2026 — the four primitive keys the backend accepts as query
  // params. Everything else must ride via filter_json or FastAPI silently
  // drops it (the source of the "shown 0 of 100 (client filter)" bug on
  // account_id, and the 2369-vs-9 bulk-preview mismatch on system_sku).
  const PRIMITIVE_FILTER_KEYS = useMemo(() => new Set([
    "marketplace", "account_id", "working_status", "china_order_status",
  ]), []);

  /** Split ``filter`` into ``{stringOnly, richFilter}`` the way ``list_orders``
   *  expects. Used by both the table load and the bulk preview so the two
   *  paths can never disagree about what "the current filter" means. */
  const buildFilterParams = () => {
    const stringOnly = Object.fromEntries(
      Object.entries(filter)
        .filter(([k, v]) => PRIMITIVE_FILTER_KEYS.has(k))
        .filter(([, v]) => v !== null && v !== undefined)
        // Only strings / arrays / primitives ride as query params; a rich
        // {in:[...]} object for a primitive key MUST go via filter_json.
        .filter(([, v]) => typeof v === "string" || Array.isArray(v)
                          || typeof v === "number" || typeof v === "boolean")
        .map(([k, v]) => {
          if (Array.isArray(v)) return [k, v.length ? v.join(",") : ""];
          return [k, typeof v === "string" ? v : String(v)];
        })
        .filter(([, v]) => v !== "")
    );
    const richFilter = Object.fromEntries(
      Object.entries(filter)
        .filter(([k, v]) => {
          if (v === null || v === undefined || v === "") return false;
          if (Array.isArray(v)) {
            // Array-valued primitive keys are already handled above (sent
            // as a comma-joined query param). Array-valued NON-primitive
            // keys — e.g. the Warehouse filter's warehouse_name — have
            // nowhere else to go, so they must ride via filter_json too,
            // or they get silently dropped entirely (neither bucket wants
            // them).
            return v.length > 0 && !PRIMITIVE_FILTER_KEYS.has(k);
          }
          if (typeof v === "object") return true;          // ranges, {in:[...]}, {empty}
          // Non-primitive string filters must go via filter_json so the backend
          // actually applies them across the full dataset.
          return typeof v === "string" && !PRIMITIVE_FILTER_KEYS.has(k);
        })
        // Array values ride as {in:[...]} — the shape both
        // _resolve_orders_warehouse_filter and _translate_filter_to_mongo
        // already expect for list-valued filters.
        .map(([k, v]) => (Array.isArray(v) ? [k, { in: v }] : [k, v]))
    );
    return { stringOnly, richFilter };
  };

  const load = async () => {
    setLoading(true);
    try {
      const { stringOnly, richFilter } = buildFilterParams();
      const params = { ...stringOnly, paginated: 1, page, page_size: pageSize };
      if (q) params.q = q;
      if (Object.keys(richFilter).length > 0) {
        params.filter_json = JSON.stringify(richFilter);
        params.filter_types_json = JSON.stringify(filterTypesMap);
      }
      // Feb 27 2026 — sort dropdown (server-side). sortKey format:
      // "<field>:<asc|desc>". Empty string → don't send params → backend
      // default (`_system_id_num desc`) applies.
      if (sortKey) {
        const [sf, sd] = sortKey.split(":");
        if (sf) { params.sort_by = sf; params.sort_dir = sd || "desc"; }
      }
      const { data } = await api.get("/orders", { params });
      const items = Array.isArray(data) ? data : (data.items || []);
      setRows(items);
      setTotal(Array.isArray(data) ? items.length : (data.total ?? 0));
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, page, pageSize, sortKey]);

  // Feb 27 2026 — load per-user column preferences once on mount so the
  // grid can reorder / hide columns without waiting on a second render.
  useEffect(() => {
    api.get("/auth/preferences/orders_columns")
      .then((r) => setColumnPref(r.data?.value || {}))
      .catch(() => setColumnPref({}));  // fall through to defaults on error
  }, []);

  // When the filter map changes, snap back to page 1 — otherwise a user
  // sitting on page 5 of a 200-row scan can end up on an empty page after
  // narrowing the filtered set to a handful of rows.
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filter)]);

  const onPageSizeChange = (n) => {
    localStorage.setItem("orders_page_size", String(n));
    setPageSize(n);
    setPage(1);
  };
  const handleSearch = () => { setPage(1); load(); };

  const doWipe = async () => {
    if (wipeConfirm !== "WIPE ALL ORDERS") {
      toast.error('Type exactly: WIPE ALL ORDERS');
      return;
    }
    setWiping(true);
    try {
      const { data } = await api.post("/orders/admin/wipe", { confirm: "WIPE ALL ORDERS" });
      toast.success(`Deleted ${data.deleted} orders — counters reset`);
      setShowWipe(false);
      setWipeConfirm("");
      setPage(1);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setWiping(false);
  };

  // Count how many orders each phone has in the loaded list — cheap client-side calc
  const phoneCounts = useMemo(() => {
    const c = {};
    rows.forEach((r) => {
      const p = (r.buyer_phone_no || r.ship_phone_number || "").trim();
      if (p) c[p] = (c[p] || 0) + 1;
    });
    return c;
  }, [rows]);

  // Client-side filter pass — the backend now handles rich shapes via
  // filter_json, but we keep this as a defensive second-pass so anything the
  // server didn't understand (older shapes, custom local overrides) still
  // narrows down correctly on top of the already-filtered page.
  // Aug 26 2026 — `warehouse_name` is excluded here: it isn't a field that
  // exists on the order row itself (it's resolved server-side by joining
  // account_id → accounts.warehouse_name, see _resolve_orders_warehouse_filter
  // in routes_orders.py). Re-checking it client-side against `row.warehouse_name`
  // (always undefined) rejected every row, so a warehouse filter that the
  // server correctly narrowed (e.g. "154 matching rows") still rendered an
  // empty table.
  const filteredRows = useMemo(
    () => {
      const { warehouse_name, ...clientFilter } = filter;
      return rows.filter((r) => applyFieldFilters(r, clientFilter, filterFields));
    },
    [rows, filter, filterFields]
  );

  // Server handles both filtering AND pagination now — no more slicing
  // filteredRows locally. `rows` is already the current page of the filtered
  // set, and `total` is the true filtered total from the backend.
  const displayRows = filteredRows;
  const displayTotal = total;

  // Feb 27 2026 — inline edit for pipeline / claim / shipping / classification
  // fields. Whitelisted below; other columns render unchanged.
  //   field       → type + optional dropdown key on `dropdowns`
  const INLINE_EDIT_SPEC = {
    final_transfer_price: { type: "number" },
    importrend_status: { type: "select", options: dropdowns.importrend_status || [] },
    marketplace_status: { type: "select", options: dropdowns.marketplace_status || [] },
    shipping_status: { type: "select", options: dropdowns.shipping_status || [] },
    claim_status: { type: "select", options: dropdowns.claim_status || [] },
    claim_raised_date: { type: "date" },
    claim_amount: { type: "number" },
    shipping_date: { type: "date" },
    courier_name: { type: "text" },
    tracking_id: { type: "text" },
    electrical_type: { type: "select", options: dropdowns.electrical_type || [] },
    hsn: { type: "text" },
  };
  const patchInline = async (orderId, field, value) => {
    try {
      // Cast to number if the field is numeric — otherwise the backend
      // stores "499" as a string and downstream compute breaks.
      let payloadVal = value;
      if (INLINE_EDIT_SPEC[field]?.type === "number") {
        payloadVal = value === "" ? null : Number(value);
      }
      await api.patch(`/orders/${orderId}`, { [field]: payloadVal });
      // Optimistic in-place update so the row updates without a full reload.
      setRows((prev) => prev.map((r) => r.id === orderId ? { ...r, [field]: payloadVal } : r));
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Feb 27 2026 — Bulk Update bar helpers. Everything below is gated by
  // `canBulkEdit("orders")` in the JSX so the bar is invisible for users
  // without the permission.
  const BULK_FIELD_SPECS = {
    ...INLINE_EDIT_SPEC,
    // Sep 8 2026 — bulk-only additions. NOT in INLINE_EDIT_SPEC because
    // the inline-edit UX on those columns hasn't been enabled yet.
    // Backend rules:
    //  • china_order_status → same per-order rules as a single-edit PATCH
    //  • working_status → bulk path leaves ``working_status_manual``
    //    unchanged so automatic Daily-Ship handling keeps running
    china_order_status: { type: "select", options: dropdowns.china_order_status || [] },
    working_status: { type: "select", options: dropdowns.working_status || [] },
  };
  const BULK_FIELD_LABELS = {
    final_transfer_price: "Final Transfer Price",
    importrend_status: "Importrend Status",
    marketplace_status: "Marketplace Status",
    shipping_status: "Shipping Status",
    china_order_status: "China Status",
    working_status: "Working Status",
    claim_status: "Claim Status",
    claim_amount: "Claim Amount",
    claim_raised_date: "Claim Raised Date",
    shipping_date: "Shipping Date",
    tracking_id: "Tracking ID",
    courier_name: "Courier Name",
    hsn: "HSN",
    electrical_type: "Electrical / Non-Electrical",
  };
  const _isBlankValue = (v) => v === null || v === undefined || v === "" || (typeof v === "number" && v === 0);
  // Are ANY filters / search active? Used to warn "This will update ALL orders"
  // when the user hasn't narrowed the set at all.
  const bulkHasFilterActive = !!(
    q || (filter && Object.values(filter).some((v) => v !== "" && v !== null && v !== undefined))
  );
  const runBulkPreview = async () => {
    // Sep 25 2026 (ticket #258251) -- was blocking every field except
    // electrical_type from ever being bulk-set to blank. Customer spec:
    // bulk edit must accept an empty value to clear a field, same as a
    // single inline edit already can.
    if (!bulkField) return;
    setBulkRunning(true);
    try {
      // Feb 28 2026 — Bug fix (two parts):
      //   1. Row-selection wins: if the user has ticked specific rows, the
      //      bulk update applies only to those IDs (no server query).
      //   2. Otherwise reuse `buildFilterParams()` — the same helper the
      //      table uses — so the confirmation count exactly matches what the
      //      user sees on-screen. The previous impl sent non-primitive text
      //      filters (system_sku, etc.) as query params, which FastAPI
      //      silently dropped, so the count was "all orders".
      if (selectedIds.size > 0) {
        // Row-selection mode — apply to explicitly ticked rows only.
        const ids = [...selectedIds];
        let candidates = rows.filter((r) => selectedIds.has(r.id));
        if (bulkMode === "blanks") {
          candidates = candidates.filter((o) => _isBlankValue(o[bulkField]));
        }
        setBulkConfirm({ count: candidates.length, ids: candidates.map((o) => o.id), mode: bulkMode, scope: "selection" });
        return;
      }
      const { stringOnly, richFilter } = buildFilterParams();
      const params = { ...stringOnly, paginated: 0, limit: 5000 };
      if (q) params.q = q;
      if (Object.keys(richFilter).length) {
        params.filter_json = JSON.stringify(richFilter);
        params.filter_types_json = JSON.stringify(filterTypesMap);
      }
      const { data } = await api.get("/orders", { params });
      let candidates = Array.isArray(data) ? data : (data.items || []);
      if (bulkMode === "blanks") {
        candidates = candidates.filter((o) => _isBlankValue(o[bulkField]));
      }
      const ids = candidates.map((o) => o.id).filter(Boolean);
      setBulkConfirm({ count: ids.length, ids, mode: bulkMode, scope: "filter" });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkRunning(false); }
  };
  const runBulkApply = async () => {
    if (!bulkConfirm) return;
    setBulkRunning(true);
    try {
      const { data } = await api.post("/orders/admin/bulk-update", {
        ids: bulkConfirm.ids,
        field: bulkField,
        value: bulkValue,
      });
      toast.success(`Updated ${data.updated} order${data.updated === 1 ? "" : "s"} successfully.${data.failed ? ` ${data.failed} failed.` : ""}`);
      setBulkConfirm(null);
      setBulkField(""); setBulkValue("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkRunning(false); }
  };

  return (
    <>
      <PageHeader title="Orders" subtitle="Full lifecycle: marketplace → China → shipping" testId={T.moduleTitle("orders")} module="orders">
        {/* ── LEFT GROUP: Sync + Density + divider + Search + filters ── */}
        {isAdmin && (
          <div className="inline-flex items-center gap-1.5">
            <Button
              testId="orders-gmail-sync-btn"
              variant="success"
              onClick={async () => {
                try {
                  const { data } = await api.post("/gmail/sync");
                  if (data.queued === false) {
                    toast.info(data.message || "Sync already running — check Settings › Gmail for progress");
                  } else {
                    toast.success(data.message || "Gmail sync started — orders will update in the background");
                  }
                  setTimeout(refreshGmailStatus, 3000);
                  setTimeout(refreshGmailStatus, 15000);
                } catch (e) { toast.error(formatApiError(e)); }
              }}
            >
              <Mail size={13} /> Sync From Gmail
            </Button>
            {gmailAgeLabel && (
              <span
                data-testid="orders-gmail-last-sync-badge"
                title={`Last successful Gmail sync: ${new Date(gmailLastRun).toLocaleString()}`}
                className={`inline-flex items-center gap-1 h-6 px-1.5 text-[10.5px] font-mono font-semibold rounded-sm border ${
                  gmailAgeTone === "emerald" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                  : gmailAgeTone === "amber" ? "bg-amber-50 text-amber-800 border-amber-200"
                  : gmailAgeTone === "red" ? "bg-red-50 text-red-800 border-red-200"
                  : "bg-slate-50 text-slate-600 border-slate-200"
                }`}
              >
                <span className={`inline-block h-1.5 w-1.5 rounded-full ${
                  gmailLastStatus === "running" ? "bg-blue-500 animate-pulse"
                  : gmailAgeTone === "emerald" ? "bg-emerald-500"
                  : gmailAgeTone === "amber" ? "bg-amber-500"
                  : gmailAgeTone === "red" ? "bg-red-500"
                  : "bg-slate-400"
                }`} />
                {gmailLastStatus === "running" ? "syncing…" : gmailAgeLabel}
              </span>
            )}
            <Button
              testId="orders-sync-allocations-btn"
              variant="info"
              disabled={syncingAllocations}
              onClick={async () => {
                setSyncingAllocations(true);
                try {
                  const { data } = await api.post("/orders/admin/sync-allocations");
                  if (!data.scanned) {
                    toast.info("No eligible unallocated orders found.");
                  } else {
                    const parts = [];
                    if (data.inventory_allocated) parts.push(`${data.inventory_allocated} from inventory`);
                    if (data.procurement_allocated) parts.push(`${data.procurement_allocated} from procurement`);
                    const allocatedMsg = parts.length ? parts.join(", ") : "none";
                    toast.success(
                      `Checked ${data.scanned} order${data.scanned === 1 ? "" : "s"} — allocated ${allocatedMsg}.` +
                      (data.still_unallocated ? ` ${data.still_unallocated} still unallocated.` : "")
                    );
                  }
                  if (data.errors && data.errors.length) {
                    toast.error(`${data.errors.length} order(s) failed during sync — check logs.`);
                  }
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setSyncingAllocations(false); }
              }}
            >
              <RefreshCw size={13} /> {syncingAllocations ? "Checking…" : "Sync Allocations"}
            </Button>
          </div>
        )}
        <div className="inline-flex border border-slate-200 rounded-sm overflow-hidden h-8" data-testid="orders-density-toggle">
          <button onClick={() => setDensityPersist("compact")} data-testid="orders-density-compact"
            className={`h-8 px-3 text-[12px] font-semibold inline-flex items-center gap-1.5 ${density === "compact" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            title="Compact view — trader's daily lifecycle columns">
            <LayoutList size={13} /> Compact
          </button>
          <button onClick={() => setDensityPersist("wide")} data-testid="orders-density-wide"
            className={`h-8 px-3 text-[12px] font-semibold inline-flex items-center gap-1.5 border-l border-slate-200 ${density === "wide" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
            title="Wide view — accountant's full P&L columns">
            <Table2 size={13} /> Wide
          </button>
        </div>
        <span className="inline-block h-6 w-px bg-slate-200 mx-0.5" aria-hidden="true" />
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid={T.searchInput("orders")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Search order id, ASIN, SKU, tracking…"
            className="h-8 pl-7 pr-2 w-64 text-[13px] border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        {canView("orders", "marketplace") && (
          <MultiSelectFilter
            label="Marketplace"
            options={dropdowns.marketplace || []}
            value={Array.isArray(filter.marketplace) ? filter.marketplace : (filter.marketplace ? [filter.marketplace] : [])}
            onChange={(next) => setFilter((f) => ({ ...f, marketplace: next.length ? next : undefined }))}
            testId="orders-filter-marketplace"
          />
        )}
        {/* Mar 1 2026 — Warehouse filter behaves exactly like Marketplace: a
            multi-select in the top bar that pushes `warehouse_name` into the
            server-side filter. The backend resolves it to the matching
            account_ids (accounts.warehouse_name → account_id ∈ [...]) since
            orders don't carry warehouse_name of their own. */}
        {canView("orders", "account_id") && (
          <MultiSelectFilter
            label="Warehouse"
            options={(() => {
              // Mar 1 2026 — narrow the option list to the user's allowlist.
              const all = filterDropdowns.warehouse_name || warehouses.map((w) => w.name).filter(Boolean);
              const allow = allowedWarehouses();
              return allow ? all.filter((n) => allow.includes(n)) : all;
            })()}
            value={Array.isArray(filter.warehouse_name) ? filter.warehouse_name : (filter.warehouse_name ? [filter.warehouse_name] : [])}
            onChange={(next) => setFilter((f) => ({ ...f, warehouse_name: next.length ? next : undefined }))}
            testId="orders-filter-warehouse"
          />
        )}
        {canView("orders", "china_order_status") && (
          <MultiSelectFilter
            label="China Status"
            options={dropdowns.china_order_status || []}
            value={Array.isArray(filter.china_order_status) ? filter.china_order_status : (filter.china_order_status ? [filter.china_order_status] : [])}
            onChange={(next) => setFilter((f) => ({ ...f, china_order_status: next.length ? next : undefined }))}
            testId="orders-filter-china-status"
          />
        )}
        <Button onClick={() => setShowFilterBar((s) => !s)} testId="filter-orders-btn">
          <Filter size={13} /> Filters
          {Object.keys(filter).filter((k) => filter[k] && !["marketplace", "china_order_status", "warehouse_name"].includes(k) && !(Array.isArray(filter[k]) && filter[k].length === 0)).length > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9.5px] font-bold text-white bg-blue-600 rounded-full">
              {Object.keys(filter).filter((k) => filter[k] && !["marketplace", "china_order_status", "warehouse_name"].includes(k) && !(Array.isArray(filter[k]) && filter[k].length === 0)).length}
            </span>
          )}
        </Button>
        <FilterPresets moduleKey="orders" value={filter} onLoad={setFilter} />
        {/* ── SPACER pushes the right group to the far edge ── */}
        <div className="flex-1" aria-hidden="true" />
        {/* ── RIGHT GROUP: icon-only cluster + divider + primary actions ── */}
        {canBulkEdit("orders") && (
          <button
            type="button"
            onClick={() => document.querySelector('[data-testid="orders-bulk-update-bar"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
            data-testid="orders-bulk-icon-btn"
            title="Bulk Update"
            className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 rounded-sm bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            <SlidersHorizontal size={14} />
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowColumnPanel(true)}
          data-testid="orders-columns-btn"
          title="Columns"
          className="h-8 w-8 inline-flex items-center justify-center border border-slate-200 rounded-sm bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        >
          <Columns size={14} />
        </button>
        <SortMenu
          value={sortKey}
          onChange={(next) => { setSortKey(next); try { localStorage.setItem("orders_sort_key", next); } catch (_) {} }}
        />
        <MoreActionsMenu
          isAdmin={isAdmin}
          bulkPrintLabels={bulkPrintLabels}
          downloadTemplate={downloadTemplate}
          onBackfill={() => { setShowBackfill(true); setBackfillResult(null); setBackfillDryRun(true); }}
          onAllocatedShipped={async () => {
            const c = window.prompt('Sweep every Seller-Flex, Easy-Ship and Flipkart order that already has inventory or procurement allocated but was never marked "Shipped". Self-Ship orders are NOT touched. Cancelled / Refunded rows are left alone.\n\nType "BACKFILL ALLOCATED SHIPPED" to proceed (or click Cancel).');
            if (c !== "BACKFILL ALLOCATED SHIPPED") return;
            try {
              const preview = await api.post("/orders/admin/backfill-allocated-shipped", { confirm: c, dry_run: true });
              const n = preview.data.considered;
              if (!n) { toast.info("No orders need backfill — everything already Shipped."); return; }
              const bm = preview.data.by_marketplace || {};
              const breakdown = Object.entries(bm).map(([k, v]) => `${k}: ${v}`).join(", ") || "—";
              if (!window.confirm(`This will flip ${n} order${n === 1 ? "" : "s"} to Shipped.\n\nBreakdown: ${breakdown}\n\nProceed?`)) return;
              const { data } = await api.post("/orders/admin/backfill-allocated-shipped", { confirm: c, dry_run: false });
              toast.success(`Flipped ${data.flipped} order${data.flipped === 1 ? "" : "s"} to Shipped.`);
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          onReclassify={() => { setShowReclassify(true); setReclassifyResult(null); setReclassifyDryRun(true); }}
          onFetchSku={async () => {
            try {
              const { data } = await api.post("/orders/admin/fetch-system-sku", {});
              toast.success(`Filled ${data.filled} system_sku value(s) from mappings (${data.considered} considered, ${data.skipped_no_mapping} still missing a mapping).`);
              load();
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          onWipe={() => setShowWipe(true)}
        />
        <span className="inline-block h-6 w-px bg-slate-200 mx-0.5" aria-hidden="true" />
        <Button onClick={() => downloadFile("/reports/all-orders", "all_orders.csv")} testId={T.exportBtn("orders")}>
          <Download size={13} /> Export
        </Button>
        <input
          ref={importFileRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleImportFile}
          className="hidden"
          data-testid="orders-import-file"
        />
        <button
          onClick={() => importFileRef.current?.click()}
          disabled={importing}
          data-testid="orders-import-btn"
          title="Upload a filled orders template (CSV or XLSX) — all fields supported"
          className="h-8 px-3 text-[12px] font-semibold inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <Upload size={13} /> {importing ? "Importing…" : "Import"}
        </button>
        <Button variant="primary" onClick={() => navigate("/orders/new")} testId={T.newBtn("orders")}>
          <Plus size={13} /> New
        </Button>
        {isAdmin && selectedIds.size > 0 && (
          <Button
            onClick={() => { setConfirmDeleteSelected(true); setConfirmDeleteText(""); }}
            testId="orders-delete-selected-btn"
            variant="danger"
            title={`Permanently delete ${selectedIds.size} selected order${selectedIds.size === 1 ? "" : "s"}`}
          >
            <AlertTriangle size={13} /> Delete Selected · {selectedIds.size}
          </Button>
        )}
      </PageHeader>
      <PageBody>
        {canBulkEdit("orders") && (
          <div className="mb-2 border border-blue-200 bg-blue-50/60 rounded-sm px-3 py-2 flex flex-wrap items-center gap-2" data-testid="orders-bulk-update-bar">
            <span className="text-[11px] uppercase tracking-wider text-blue-900 font-bold shrink-0">Bulk Update</span>
            <select
              data-testid="bulk-field-select"
              value={bulkField}
              onChange={(e) => { setBulkField(e.target.value); setBulkValue(""); }}
              className="h-8 px-2 text-[12.5px] border border-slate-200 rounded-sm bg-white min-w-[190px]"
            >
              <option value="">Select field</option>
              {Object.entries(BULK_FIELD_LABELS).map(([k, lbl]) => (
                <option key={k} value={k}>{lbl}</option>
              ))}
            </select>
            {bulkField && (() => {
              const spec = BULK_FIELD_SPECS[bulkField];
              if (bulkField === "electrical_type") {
                return (
                  <div className="inline-flex h-8 items-center border border-slate-200 rounded-sm bg-white overflow-hidden">
                    {["Electrical", "Non-Electrical"].map((opt) => (
                      <button key={opt} type="button" data-testid={`bulk-toggle-${opt}`}
                        onClick={() => setBulkValue(opt)}
                        className={`h-full px-3 text-[12px] font-semibold ${bulkValue === opt ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                      >{opt}</button>
                    ))}
                  </div>
                );
              }
              if (spec?.type === "select") {
                return (
                  <select data-testid="bulk-value-select" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)}
                    className="h-8 px-2 text-[12.5px] border border-slate-200 rounded-sm bg-white min-w-[160px]">
                    <option value="">Select value</option>
                    {(spec.options || []).map((opt) => {
                      const val = typeof opt === "string" ? opt : opt.value;
                      const lbl = typeof opt === "string" ? opt : (opt.label || opt.value);
                      return <option key={val} value={val}>{lbl}</option>;
                    })}
                  </select>
                );
              }
              return (
                <input
                  data-testid="bulk-value-input"
                  type={spec?.type === "number" ? "number" : (spec?.type === "date" ? "date" : "text")}
                  value={bulkValue}
                  onChange={(e) => setBulkValue(e.target.value)}
                  placeholder={spec?.type === "date" ? "yyyy-mm-dd" : "Value"}
                  className="h-8 px-2 text-[12.5px] border border-slate-200 rounded-sm bg-white min-w-[160px]"
                />
              );
            })()}
            <div className="inline-flex h-8 items-center border border-slate-200 rounded-sm bg-white overflow-hidden">
              <button type="button" data-testid="bulk-mode-all"
                onClick={() => setBulkMode("all")}
                className={`h-full px-3 text-[12px] font-semibold ${bulkMode === "all" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                title="Update every filtered order"
              >Update all</button>
              <button type="button" data-testid="bulk-mode-blanks"
                onClick={() => setBulkMode("blanks")}
                className={`h-full px-3 text-[12px] font-semibold border-l border-slate-200 ${bulkMode === "blanks" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
                title="Only update filtered orders where this field is blank"
              >Fill blanks only</button>
            </div>
            <Button
              variant="primary"
              onClick={runBulkPreview}
              testId="bulk-apply-btn"
              disabled={bulkRunning || !bulkField}
            >
              {bulkRunning ? "…" : "Set for filtered orders"}
            </Button>
            {!bulkHasFilterActive && (
              <span className="text-[11px] font-semibold text-red-700 ml-auto" data-testid="bulk-no-filter-warning">
                ⚠ No filter active — will target ALL orders
              </span>
            )}
          </div>
        )}
        {showFilterBar && (
          <div className="mb-3">
            <FieldFilterBar
              moduleKey="orders"
              fields={filterFields}
              dropdowns={filterDropdowns}
              value={filter}
              onChange={setFilter}
              rows={rows}
            />
          </div>
        )}
        <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
          <div className="max-h-[calc(100vh-200px)] overflow-auto">
            {(() => {
              const COLS = [
                // Feb 27 2026 — column order per user spec: Image → Created Date →
                // Account → Marketplace ID → System SKU → Product → Qty → Order ID →
                // Item ₹ → Final Transfer Price ₹ → Importrend/Marketplace/China/Shipping
                // status; every other column follows.
                { k: "image", label: "Image", field: "image_url", render: (o) => <td>{o.image_url ? <ImageThumb src={o.image_url} size={64} testId={`order-thumb-${o.id}`} /> : "—"}</td> },
                { k: "created_date", label: "Created Date", field: "created_at", render: (o) => <td className="font-mono whitespace-nowrap">{formatDMY(o.created_at)}</td> },
                { k: "account_id", label: "Account", field: "account_id", render: (o) => <td className="font-mono">{o.account_id || "—"}</td> },
                { k: "marketplace_identifier", label: "Marketplace ID", field: "marketplace_identifier", render: (o) => (
                  <td className="font-mono">
                    {o.marketplace_identifier ? (
                      o.product_url ? (
                        <a href={o.product_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:underline inline-flex items-center gap-1">
                          {o.marketplace_identifier} <ExternalLink size={11} />
                        </a>
                      ) : o.marketplace_identifier
                    ) : "—"}
                  </td>
                ) },
                { k: "system_sku", label: "System SKU", field: "system_sku", render: (o) => <td className="font-mono">{o.system_sku || "—"}</td> },
                { k: "product_name", label: "Product", field: "product_name", render: (o) => <td className="truncate max-w-[240px]">{o.product_name || "—"}</td> },
                { k: "qty_purchased", label: "Qty", field: "qty_purchased", render: (o) => <td className="font-mono">{o.qty_purchased ?? "—"}</td> },
                { k: "order_id", label: "Order ID", field: "order_id", render: (o) => <td className="font-mono">{o.order_id || "—"}</td> },
                { k: "item_price", label: "Item ₹", field: "item_price", render: (o) => <td className="font-mono">{o.item_price ?? "—"}</td> },
                { k: "final_transfer_price", label: "Final Transfer Price ₹", field: "final_transfer_price",
                  render: (o) => {
                    const raw = o.final_transfer_price;
                    const isBlank = raw === null || raw === undefined || raw === "" || Number(raw) === 0;
                    const display = isBlank
                      ? <span className="text-slate-400">—</span>
                      : Number(raw).toFixed(2);
                    return <InlineEditCell
                      value={raw ?? ""}
                      spec={INLINE_EDIT_SPEC.final_transfer_price}
                      display={display}
                      className="font-mono"
                      testId={`inline-ftp-${o.id}`}
                      onSave={(v) => patchInline(o.id, "final_transfer_price", v)}
                    />;
                  } },
                { k: "importrend_status", label: "Importrend", field: "importrend_status", render: (o) => (
                  <InlineEditCell value={o.importrend_status ?? ""} spec={INLINE_EDIT_SPEC.importrend_status}
                    display={<StatusPill value={o.importrend_status} tone={statusTone(o.importrend_status)} />}
                    testId={`inline-ir-${o.id}`} onSave={(v) => patchInline(o.id, "importrend_status", v)} />
                ) },
                { k: "marketplace_status", label: "Marketplace Status", field: "marketplace_status", render: (o) => (
                  <InlineEditCell value={o.marketplace_status ?? ""} spec={INLINE_EDIT_SPEC.marketplace_status}
                    display={<StatusPill value={o.marketplace_status} tone={statusTone(o.marketplace_status)} />}
                    testId={`inline-mp-${o.id}`} onSave={(v) => patchInline(o.id, "marketplace_status", v)} />
                ) },
                { k: "china_order_status", label: "China Status", field: "china_order_status", render: (o) => <td><StatusPill value={o.china_order_status} tone={statusTone(o.china_order_status)} /></td> },
                { k: "shipping_status", label: "Shipping", field: "shipping_status", render: (o) => (
                  <InlineEditCell value={o.shipping_status ?? ""} spec={INLINE_EDIT_SPEC.shipping_status}
                    display={<StatusPill value={o.shipping_status} tone={statusTone(o.shipping_status)} />}
                    testId={`inline-ss-${o.id}`} onSave={(v) => patchInline(o.id, "shipping_status", v)} />
                ) },
                // ── Rest of the fields (unchanged relative order) ───────────
                { k: "system_id", label: "System ID", field: null, render: (o) => <td className="font-mono">#{o.system_id}</td> },
                { k: "marketplace", label: "Marketplace", field: "marketplace", render: (o) => <td>{o.marketplace || "—"}</td> },
                { k: "customer", label: "Customer", field: "buyer_phone_no", render: (o) => {
                  const p = (o.buyer_phone_no || o.ship_phone_number || "").trim();
                  const count = p ? phoneCounts[p] : 0;
                  const name = o.recipient_name || o.buyer_email || (p ? p.slice(-4) : "—");
                  if (!p) return <td><span className="text-slate-400">—</span></td>;
                  if (count > 1) return (
                    <td>
                      <span
                        data-testid={`repeat-tag-${o.id}`}
                        className="inline-flex items-center gap-1 h-5 px-1.5 text-[10.5px] font-semibold text-amber-900 bg-amber-50 border border-amber-300 rounded-sm"
                        title={`${count} orders from this customer (loaded set)`}
                      >
                        <Repeat size={10} /> {name}
                        <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[9.5px] font-bold text-white bg-amber-600 rounded-full">×{count}</span>
                      </span>
                    </td>
                  );
                  return <td><span className="truncate max-w-[140px] inline-block text-[12px]">{name}</span></td>;
                } },
                { k: "working_status", label: "Working", field: "working_status", render: (o) => <td><StatusPill value={o.working_status} tone={statusTone(o.working_status)} /></td> },
                { k: "china_transfer_price", label: "Transfer ₹", field: "china_transfer_price", render: (o) => <td className="font-mono">{Number(o.china_transfer_price || 0).toFixed(2)}</td> },
                { k: "calculated_sp", label: "Calc SP ₹", field: "calculated_sp", render: (o) => <td className="font-mono">{Number(o.calculated_sp || 0).toFixed(2)}</td> },
                // --- China procurement inputs ---
                // Sep 25 2026 (ticket #258251) -- China No. (china_id) was
                // available as a filter but never actually shown as a column
                // in the table itself, despite being requested before.
                { k: "china_id", label: "China No", field: "china_id", render: (o) => <td className="font-mono">{o.china_id ?? "—"}</td> },
                { k: "china_order_id", label: "China Order ID", field: "china_order_id", render: (o) => <td className="font-mono">{o.china_order_id || "—"}</td> },
                { k: "china_shipment_id", label: "China Shipment", field: "china_shipment_id", render: (o) => <td className="font-mono text-indigo-800">{o.china_shipment_id || "—"}</td> },
                { k: "china_order_date", label: "China Order Date", field: "china_order_date", render: (o) => <td className="font-mono whitespace-nowrap">{formatDMY(o.china_order_date)}</td> },
                { k: "china_order_dispatched_date", label: "China Dispatched", field: "china_order_dispatched_date", render: (o) => <td className="font-mono whitespace-nowrap">{formatDMY(o.china_order_dispatched_date)}</td> },
                { k: "china_order_received_date", label: "China Received", field: "china_order_received_date", render: (o) => <td className="font-mono whitespace-nowrap">{formatDMY(o.china_order_received_date)}</td> },
                { k: "china_price", label: "China ¥", field: "china_price", render: (o) => <td className="font-mono">{o.china_price ?? "—"}</td> },
                { k: "china_shipping", label: "China Ship ¥", field: "china_shipping", render: (o) => <td className="font-mono">{o.china_shipping ?? "—"}</td> },
                { k: "china_weight_gram", label: "Wt (g)", field: "china_weight_gram", render: (o) => <td className="font-mono">{o.china_weight_gram ?? "—"}</td> },
                { k: "china_qty_to_order", label: "China Qty", field: "china_qty_to_order", render: (o) => <td className="font-mono">{o.china_qty_to_order ?? "—"}</td> },
                { k: "buy_back_price", label: "Buy Back ₹", field: "buy_back_price", render: (o) => <td className="font-mono">{o.buy_back_price ?? "—"}</td> },
                { k: "china_landing_price", label: "Landing ₹", field: "china_landing_price", render: (o) => <td className="font-mono">{Number(o.china_landing_price || 0).toFixed(2)}</td> },
                { k: "china_margin", label: "China Margin ₹", field: "china_margin", render: (o) => <td className="font-mono">{Number(o.china_margin || 0).toFixed(2)}</td> },
                { k: "variant", label: "Variant", field: "variant", render: (o) => <td>{o.variant || "—"}</td> },
                { k: "source_url", label: "Source URL", field: "source_url", render: (o) => <td className="font-mono truncate max-w-[160px]">{o.source_url ? <a href={o.source_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-blue-600 hover:underline">link</a> : "—"}</td> },
                // --- Claim ---
                { k: "claim_status", label: "Claim", field: "claim_status", render: (o) => (
                  <InlineEditCell value={o.claim_status ?? ""} spec={INLINE_EDIT_SPEC.claim_status}
                    display={<StatusPill value={o.claim_status} tone={statusTone(o.claim_status)} />}
                    testId={`inline-claim-${o.id}`} onSave={(v) => patchInline(o.id, "claim_status", v)} />
                ) },
                { k: "claim_amount", label: "Claim ₹", field: "claim_amount", render: (o) => (
                  <InlineEditCell value={o.claim_amount ?? ""} spec={INLINE_EDIT_SPEC.claim_amount}
                    className="font-mono" display={o.claim_amount ?? "—"}
                    testId={`inline-claim-amt-${o.id}`} onSave={(v) => patchInline(o.id, "claim_amount", v)} />
                ) },
                { k: "claim_raised_date", label: "Claim Raised", field: "claim_raised_date", render: (o) => (
                  <InlineEditCell value={o.claim_raised_date ?? ""} spec={INLINE_EDIT_SPEC.claim_raised_date}
                    className="font-mono whitespace-nowrap" display={formatDMY(o.claim_raised_date)}
                    testId={`inline-claim-date-${o.id}`} onSave={(v) => patchInline(o.id, "claim_raised_date", v)} />
                ) },
                // --- Shipping ---
                { k: "shipping_date", label: "Ship Date", field: "shipping_date", render: (o) => (
                  <InlineEditCell value={o.shipping_date ?? ""} spec={INLINE_EDIT_SPEC.shipping_date}
                    className="font-mono whitespace-nowrap" display={formatDMY(o.shipping_date)}
                    testId={`inline-shipdate-${o.id}`} onSave={(v) => patchInline(o.id, "shipping_date", v)} />
                ) },
                { k: "courier_name", label: "Courier", field: "courier_name", render: (o) => (
                  <InlineEditCell value={o.courier_name ?? ""} spec={INLINE_EDIT_SPEC.courier_name}
                    display={o.courier_name || "—"}
                    testId={`inline-courier-${o.id}`} onSave={(v) => patchInline(o.id, "courier_name", v)} />
                ) },
                { k: "tracking_id", label: "Tracking", field: "tracking_id", render: (o) => (
                  <InlineEditCell value={o.tracking_id ?? ""} spec={INLINE_EDIT_SPEC.tracking_id}
                    className="font-mono" display={o.tracking_id || "—"}
                    testId={`inline-tracking-${o.id}`} onSave={(v) => patchInline(o.id, "tracking_id", v)} />
                ) },
                { k: "refund_status", label: "Refund", field: "refund_status", render: (o) => <td><StatusPill value={o.refund_status} tone={statusTone(o.refund_status)} /></td> },
                { k: "return_received_status", label: "Return Status", field: "return_received_status", render: (o) => <td><StatusPill value={o.return_received_status} tone={statusTone(o.return_received_status)} /></td> },
                // --- Marketplace details ---
                { k: "order_item_id", label: "Item ID", field: "order_item_id", render: (o) => <td className="font-mono">{o.order_item_id || "—"}</td> },
                { k: "purchase_date", label: "Purchase Date", field: "purchase_date", render: (o) => <td className="font-mono whitespace-nowrap">{formatDMY(o.purchase_date)}</td> },
                { k: "buyer_email", label: "Buyer Email", field: "buyer_email", render: (o) => <td className="font-mono truncate max-w-[160px]">{o.buyer_email || "—"}</td> },
                { k: "buyer_phone_no", label: "Buyer Phone", field: "buyer_phone_no", render: (o) => <td className="font-mono">{o.buyer_phone_no || "—"}</td> },
                { k: "item_tax", label: "Item Tax ₹", field: "item_tax", render: (o) => <td className="font-mono">{o.item_tax ?? "—"}</td> },
                { k: "shipping_price", label: "Ship ₹", field: "shipping_price", render: (o) => <td className="font-mono">{o.shipping_price ?? "—"}</td> },
                { k: "recipient_name", label: "Recipient", field: "recipient_name", render: (o) => <td className="truncate max-w-[140px]">{o.recipient_name || "—"}</td> },
                { k: "ship_city", label: "Ship City", field: "ship_city", render: (o) => <td>{o.ship_city || "—"}</td> },
                { k: "ship_state", label: "Ship State", field: "ship_state", render: (o) => <td>{o.ship_state || "—"}</td> },
                { k: "ship_postal_code", label: "Pincode", field: "ship_postal_code", render: (o) => <td className="font-mono">{o.ship_postal_code || "—"}</td> },
                { k: "payment_method", label: "Payment", field: "payment_method", render: (o) => <td>{o.payment_method || "—"}</td> },
                { k: "is_prime", label: "Prime", field: "is_prime", render: (o) => <td>{o.is_prime ? "✓" : "—"}</td> },
                // --- Finance ---
                { k: "amazon_commission", label: "Commission ₹", field: "amazon_commission", render: (o) => <td className="font-mono">{o.amazon_commission ?? "—"}</td> },
                { k: "amazon_payout", label: "Payout ₹", field: "amazon_payout", render: (o) => <td className="font-mono">{o.amazon_payout ?? "—"}</td> },
                { k: "importrend_margin", label: "Margin ₹", field: "importrend_margin", render: (o) => <td className="font-mono">{o.importrend_margin ?? "—"}</td> },
                { k: "inventory_allocated", label: "Alloc.", field: "system_sku", render: (o) => {
                  const q = Number(o.inventory_allocated_qty || 0);
                  return (
                    <td data-testid={`orders-allocated-${o.id}`}>
                      {q > 0
                        ? <span className="inline-flex px-1.5 h-5 text-[9.5px] font-bold uppercase tracking-wider rounded-sm border bg-emerald-50 text-emerald-800 border-emerald-300">Yes · {q}</span>
                        : <span className="text-slate-400">No</span>}
                    </td>
                  );
                } },
                { k: "gst_rate", label: "GST %", field: "gst_rate", render: (o) => <td className="font-mono">{o.gst_rate ?? "—"}</td> },
                { k: "electrical_type", label: "Electrical", field: "electrical_type", render: (o) => (
                  <InlineEditCell value={o.electrical_type ?? ""} spec={INLINE_EDIT_SPEC.electrical_type}
                    display={o.electrical_type || "—"}
                    testId={`inline-electrical-${o.id}`} onSave={(v) => patchInline(o.id, "electrical_type", v)} />
                ) },
                { k: "hsn", label: "HSN", field: "hsn", render: (o) => (
                  <InlineEditCell value={o.hsn ?? ""} spec={INLINE_EDIT_SPEC.hsn}
                    className="font-mono" display={o.hsn || "—"}
                    testId={`inline-hsn-${o.id}`} onSave={(v) => patchInline(o.id, "hsn", v)} />
                ) },
                { k: "tags", label: "Tags", field: "tags", render: (o) => <td className="truncate max-w-[120px]">{o.tags || "—"}</td> },
                { k: "comments", label: "Comments", field: "comments", render: (o) => (
                  <td className="max-w-[180px] text-slate-600 text-[11.5px] align-top py-1">
                    {o.comments ? <TranslateInline text={o.comments} testId={`orders-translate-${o.id}`} /> : "—"}
                  </td>
                ) },
                // Feb 26 2026 — packaging video preview thumbnail. Click opens
                // the video in a new tab. The video is streamed via our
                // backend (`/api/orders/{oid}/packaging-video.mp4`) so we
                // attach `?auth=<token>` — browsers can't set headers on
                // media elements.
                // Aug 6 2026 — render the real ffmpeg-extracted JPG thumbnail
                // (`/packaging-video-thumb.jpg`) as a plain <img> instead of
                // the old <video#t=0.5> guess, which loaded instantly but
                // often painted blank. Falls back to the video trick only
                // for legacy orders with no stored thumbnail.
                { k: "packaging_video_url", label: "Packaging", field: "packaging_video_url", render: (o) => {
                  if (!o.packaging_video_url) return <td data-testid={`orders-packaging-${o.id}`}><span className="text-slate-400">—</span></td>;
                  const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                  const tok = localStorage.getItem("access_token") || "";
                  const withAuth = (path) => {
                    const base = path.startsWith("http") ? path : `${API}${path}`;
                    return `${base}${base.includes("?") ? "&" : "?"}auth=${encodeURIComponent(tok)}`;
                  };
                  const src = withAuth(o.packaging_video_url);
                  const thumbSrc = o.packaging_video_thumb_url ? withAuth(o.packaging_video_thumb_url) : null;
                  return (
                    <td data-testid={`orders-packaging-${o.id}`}>
                      <a
                        href={src}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Open packaging video"
                        className="block"
                      >
                        {thumbSrc ? (
                          <img
                            src={thumbSrc}
                            alt="Packaging video thumbnail"
                            className="h-8 w-12 object-cover border border-emerald-300 rounded-sm bg-slate-100"
                          />
                        ) : (
                          <video
                            src={`${src}#t=0.5`}
                            preload="metadata"
                            muted
                            playsInline
                            className="h-8 w-12 object-cover border border-emerald-300 rounded-sm bg-slate-100"
                          />
                        )}
                      </a>
                    </td>
                  );
                } },
              ];
              const visibleCols = (() => {
                const base = COLS.filter((c) => !c.field || canView("orders", c.field));
                // Feb 27 2026 — per-user column preference override:
                //   • If the user saved a `columns` order, honour it (unknown keys are appended).
                //   • If they saved a `hidden` set, hide those too.
                //   • Compact toggle still filters on top (respected by user's choice).
                const savedOrder = columnPref?.columns;
                const hidden = new Set(columnPref?.hidden || []);
                let ordered = base;
                if (Array.isArray(savedOrder) && savedOrder.length) {
                  const byKey = Object.fromEntries(base.map((c) => [c.k, c]));
                  const seen = new Set();
                  const inOrder = [];
                  for (const k of savedOrder) {
                    if (byKey[k]) { inOrder.push(byKey[k]); seen.add(k); }
                  }
                  // Preserve columns the user hasn't ranked at the end (so new columns don't vanish).
                  for (const c of base) if (!seen.has(c.k)) inOrder.push(c);
                  ordered = inOrder;
                }
                return ordered.filter((c) => {
                  if (hidden.has(c.k)) return false;
                  if (density === "compact" && !COMPACT_COLS.has(c.k)) return false;
                  return true;
                });
              })();
              return (
                <table className="erp-table" data-testid={T.ordersTable}>
                  <thead>
                    <tr>
                      {isAdmin && (
                        <th className="w-[32px] text-center">
                          <input
                            type="checkbox"
                            data-testid="orders-select-all"
                            checked={displayRows.length > 0 && displayRows.every((o) => selectedIds.has(o.id))}
                            onChange={(e) => {
                              const next = new Set(selectedIds);
                              if (e.target.checked) displayRows.forEach((o) => next.add(o.id));
                              else displayRows.forEach((o) => next.delete(o.id));
                              setSelectedIds(next);
                            }}
                            className="cursor-pointer"
                          />
                        </th>
                      )}
                      {visibleCols.map((c) => <th key={c.k}>{c.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((o) => (
                      <tr
                        key={o.id}
                        data-testid={T.row("orders", o.id)}
                        onClick={() => window.open(`/orders/${o.id}`, "_blank")}
                        className={`cursor-pointer ${selectedIds.has(o.id) ? "bg-blue-50/60" : ""}`}
                      >
                        {isAdmin && (
                          <td className="text-center" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              data-testid={`orders-select-${o.id}`}
                              checked={selectedIds.has(o.id)}
                              onChange={(e) => {
                                const next = new Set(selectedIds);
                                if (e.target.checked) next.add(o.id);
                                else next.delete(o.id);
                                setSelectedIds(next);
                              }}
                              className="cursor-pointer"
                            />
                          </td>
                        )}
                        {visibleCols.map((c) => <React.Fragment key={c.k}>{c.render(o)}</React.Fragment>)}
                      </tr>
                    ))}
                    {!displayRows.length && !loading && (
                      <tr><td colSpan={visibleCols.length + (isAdmin ? 1 : 0)} className="text-center py-8 text-slate-500">
                        {rows.length > 0
                          ? <>No orders match the current filters. <button onClick={() => setFilter({})} className="text-blue-600 hover:underline">Clear filters</button></>
                          : <>No orders yet. <Link to="/orders/new" className="text-blue-600 hover:underline">Create one</Link> or use <Link to="/imports" className="text-blue-600 hover:underline">Imports</Link> to upload a CSV.</>
                        }
                      </td></tr>
                    )}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
        <div className="mt-2 flex items-center justify-between text-[11.5px] text-slate-600">
          <span className="font-mono" data-testid="orders-page-local-count">
            {loading
              ? <span className="inline-flex items-center gap-1.5 text-slate-500"><span className="inline-block w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                  {hasClientSideFilter ? "Applying filters on server…" : "Loading orders…"}
                </span>
              : hasClientSideFilter
                ? <>Filtered: <b>{displayTotal}</b> matching row{displayTotal === 1 ? "" : "s"} <span className="text-slate-400">(showing {displayRows.length} on this page)</span></>
                : <>Showing {filteredRows.length} of {rows.length} loaded {filteredRows.length !== rows.length && <span className="text-slate-400">(client filter)</span>}</>
            }
          </span>
          <PaginationBar
            page={page}
            pageSize={pageSize}
            total={displayTotal}
            onPageChange={setPage}
            onPageSizeChange={onPageSizeChange}
            loading={loading}
            testIdPrefix="orders"
          />
        </div>
      </PageBody>

      {importResult && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50" onClick={() => setImportResult(null)}>
          <div className="w-[560px] max-w-[95vw] max-h-[85vh] bg-white border border-slate-200 rounded-sm flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="orders-import-result-modal">
            <div className="h-12 border-b border-slate-200 px-4 flex items-center justify-between">
              <div className="font-heading text-[13.5px] font-semibold text-slate-900">
                Import complete <span className="ml-1 text-[11px] font-mono text-slate-500">· {importResult.filename}</span>
              </div>
              <button onClick={() => setImportResult(null)} data-testid="orders-import-result-close" className="text-slate-500 hover:text-slate-900">✕</button>
            </div>
            <div className="p-4 grid grid-cols-3 gap-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3">
                <div className="text-[10.5px] uppercase tracking-wider text-emerald-700 font-semibold">Created</div>
                <div className="font-mono text-2xl font-bold text-emerald-800">{importResult.created}</div>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3">
                <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold">Updated</div>
                <div className="font-mono text-2xl font-bold text-indigo-800">{importResult.updated || 0}</div>
              </div>
              <div className="bg-slate-100 border border-slate-200 rounded-sm p-3">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-600 font-semibold">Skipped</div>
                <div className="font-mono text-2xl font-bold text-slate-800">{importResult.skipped}</div>
              </div>
            </div>
            {(importResult.errors || []).length > 0 && (
              <div className="px-4 pb-4 overflow-auto">
                <div className="text-[11px] uppercase tracking-wider text-red-700 font-semibold mb-1.5">
                  Errors ({importResult.total_errors || importResult.errors.length})
                </div>
                <table className="erp-table w-full">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Row</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importResult.errors.map((er, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="font-mono text-[11px]">{er.row}</td>
                        <td className="text-[11.5px] text-red-700">{er.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
      {showReclassify && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !reclassifying && setShowReclassify(false)}
          data-testid="orders-reclassify-modal"
        >
          <div
            className="bg-white rounded-sm border-2 border-teal-500 shadow-2xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-teal-200 bg-teal-50 px-4 py-3">
              <RefreshCw size={18} className="text-teal-700" />
              <h2 className="font-heading text-[14px] font-bold text-teal-900">
                Bulk Reclassify China Status
              </h2>
            </div>
            <div className="p-4 space-y-3 text-[12.5px] text-slate-700">
              <p>
                Re-runs the Eligible / Price-Check classifier across every
                order whose current status is <b>blank</b>, <b>Eligible</b>, or{" "}
                <b>Price-Check</b>. Uses the derived{" "}
                <span className="font-mono bg-slate-100 px-1 rounded">
                  reseller_margin_calculated_percentage
                </span>
                {" "}(band <b>15 &lt; x &lt; 30</b>) after recomputing all China
                Procurement fields with the latest globals + formula overrides.
              </p>
              <p className="text-slate-500 text-[11.5px]">
                Pipeline / ops-set statuses — <b>Added, Placed, OTS, Dispatched,
                Delivered, Cancelled, Refunded</b> — are never touched. If any
                precondition (china_price, moq, china_weight_gram,
                amazon_commission, item_price, item_tax) is zero/blank the row
                is left alone.
              </p>
              <label className="flex items-center gap-2 select-none cursor-pointer bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <input
                  type="checkbox"
                  checked={reclassifyDryRun}
                  onChange={(e) => setReclassifyDryRun(e.target.checked)}
                  disabled={reclassifying}
                  data-testid="orders-reclassify-dry-run"
                  className="h-3.5 w-3.5"
                />
                <span className="text-[12px] font-semibold text-slate-800">Dry run</span>
                <span className="text-[11px] text-slate-500">
                  — preview counts without changing any data
                </span>
              </label>
              {reclassifyResult && (
                <div
                  className="border border-slate-200 rounded-sm p-3 bg-slate-50 text-[11.5px] space-y-1"
                  data-testid="orders-reclassify-result"
                >
                  <div className="font-semibold text-slate-800 mb-1">
                    {reclassifyResult.dry_run ? "Dry-run result" : "Reclassify complete"}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono">
                    <span>Considered:</span>
                    <span className="text-right">{reclassifyResult.considered}</span>
                    <span className="text-emerald-700">→ Eligible:</span>
                    <span className="text-right text-emerald-700 font-semibold">
                      {reclassifyResult.flipped_to_eligible}
                    </span>
                    <span className="text-amber-700">→ Price-Check:</span>
                    <span className="text-right text-amber-700 font-semibold">
                      {reclassifyResult.flipped_to_price_check}
                    </span>
                    <span className="text-slate-500">Cleared → blank:</span>
                    <span className="text-right text-slate-500">
                      {reclassifyResult.cleared_to_blank}
                    </span>
                    <span className="text-slate-500">Unchanged:</span>
                    <span className="text-right text-slate-500">
                      {reclassifyResult.unchanged}
                    </span>
                    <span className="text-slate-500">Skipped (preconditions):</span>
                    <span className="text-right text-slate-500">
                      {reclassifyResult.skipped_preconditions}
                    </span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
              <Button
                onClick={() => { setShowReclassify(false); setReclassifyResult(null); }}
                disabled={reclassifying}
                testId="orders-reclassify-cancel"
              >
                Close
              </Button>
              <button
                onClick={runReclassify}
                disabled={reclassifying}
                data-testid="orders-reclassify-run-btn"
                className={`h-8 px-3 text-[12px] font-bold uppercase tracking-wider rounded-sm inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${
                  reclassifyDryRun
                    ? "bg-slate-700 text-white hover:bg-slate-800"
                    : "bg-teal-600 text-white hover:bg-teal-700"
                }`}
              >
                <RefreshCw size={12} />
                {reclassifying
                  ? (reclassifyDryRun ? "Previewing…" : "Applying…")
                  : (reclassifyDryRun ? "Preview (dry-run)" : "Apply Reclassify")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showBackfill && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !backfilling && setShowBackfill(false)}
          data-testid="orders-backfill-modal"
        >
          <div
            className="bg-white rounded-sm border-2 border-indigo-500 shadow-2xl max-w-lg w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-indigo-200 bg-indigo-50 px-4 py-3">
              <History size={18} className="text-indigo-700" />
              <h2 className="font-heading text-[14px] font-bold text-indigo-900">
                Backfill Allocations
              </h2>
            </div>
            <div className="p-4 space-y-3 text-[12.5px] text-slate-700">
              <p>
                Retroactively allocates stock for historical orders where{" "}
                <b>marketplace_status is blank</b>, a{" "}
                <span className="font-mono">system_sku</span> is present, and qty &gt; 0.
              </p>
              <ul className="text-[11.5px] text-slate-600 pl-4 list-disc space-y-0.5">
                <li>
                  <b>Seller-Flex / Easy-Ship / Flipkart</b>: deducts stock and
                  flips{" "}
                  <span className="font-mono bg-slate-100 px-1 rounded">
                    marketplace_status → Shipped
                  </span>
                  .
                </li>
                <li>
                  <b>Self-ship</b> (any other account): deducts stock and flips{" "}
                  <span className="font-mono bg-slate-100 px-1 rounded">
                    working_status → Daily-Ship
                  </span>
                  . marketplace_status stays blank.
                </li>
              </ul>
              <p className="text-slate-500 text-[11.5px]">
                Rows already carrying a marketplace_status (Shipped / Cancelled /
                Refunded / etc.) are left untouched. Rows with insufficient stock
                are reported and skipped.
              </p>
              <label className="flex items-center gap-2 select-none cursor-pointer bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                <input
                  type="checkbox"
                  checked={backfillDryRun}
                  onChange={(e) => setBackfillDryRun(e.target.checked)}
                  disabled={backfilling}
                  data-testid="orders-backfill-dry-run"
                  className="h-3.5 w-3.5"
                />
                <span className="text-[12px] font-semibold text-slate-800">
                  Dry run
                </span>
                <span className="text-[11px] text-slate-500">
                  — preview counts without changing any data
                </span>
              </label>
              {backfillResult && (
                <div
                  className="border border-slate-200 rounded-sm p-3 bg-slate-50 text-[11.5px] space-y-1"
                  data-testid="orders-backfill-result"
                >
                  <div className="font-semibold text-slate-800 mb-1">
                    {backfillResult.dry_run ? "Dry-run result" : "Backfill complete"}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 font-mono">
                    <span>Considered:</span>
                    <span className="text-right">{backfillResult.considered}</span>
                    <span className="text-emerald-700">Allocated:</span>
                    <span className="text-right text-emerald-700 font-semibold">
                      {backfillResult.allocated}
                    </span>
                    <span className="text-amber-700">Skipped (no stock):</span>
                    <span className="text-right text-amber-700">
                      {backfillResult.skipped_no_inventory}
                    </span>
                    <span className="text-slate-500">Skipped (terminal):</span>
                    <span className="text-right text-slate-500">
                      {backfillResult.skipped_terminal}
                    </span>
                    <span className="text-slate-500">Skipped (no qty):</span>
                    <span className="text-right text-slate-500">
                      {backfillResult.skipped_no_qty}
                    </span>
                    {!backfillResult.dry_run && (
                      <>
                        <span className="text-indigo-700">Daily-Ship flipped:</span>
                        <span className="text-right text-indigo-700">
                          {backfillResult.daily_ship_flipped}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
              <Button
                onClick={() => { setShowBackfill(false); setBackfillResult(null); }}
                disabled={backfilling}
                testId="orders-backfill-cancel"
              >
                Close
              </Button>
              <button
                onClick={runBackfill}
                disabled={backfilling}
                data-testid="orders-backfill-run-btn"
                className={`h-8 px-3 text-[12px] font-bold uppercase tracking-wider rounded-sm inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed ${
                  backfillDryRun
                    ? "bg-slate-700 text-white hover:bg-slate-800"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                }`}
              >
                <History size={12} />
                {backfilling
                  ? (backfillDryRun ? "Previewing…" : "Applying…")
                  : (backfillDryRun ? "Preview (dry-run)" : "Apply Backfill")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showWipe && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !wiping && setShowWipe(false)} data-testid="orders-wipe-modal">
          <div className="bg-white rounded-sm border-2 border-red-500 shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-3">
              <AlertTriangle size={18} className="text-red-700" />
              <h2 className="font-heading text-[14px] font-bold text-red-900">Danger Zone — Wipe All Orders</h2>
            </div>
            <div className="p-4 space-y-3 text-[12.5px] text-slate-700">
              <p>This will <b className="text-red-700">permanently delete every order</b> in the current environment ({window.location.host}) and reset the ID counters to 0.</p>
              <p className="text-slate-500 text-[11.5px]">Use this when you want to re-import your master orders sheet from scratch.</p>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Type <span className="font-mono text-red-700 bg-red-50 px-1 rounded">WIPE ALL ORDERS</span> to confirm</span>
                <input
                  autoFocus
                  value={wipeConfirm}
                  onChange={(e) => setWipeConfirm(e.target.value)}
                  disabled={wiping}
                  data-testid="orders-wipe-confirm-input"
                  placeholder="WIPE ALL ORDERS"
                  className="mt-1 w-full h-8 px-2 text-[12.5px] font-mono border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
              <Button onClick={() => { setShowWipe(false); setWipeConfirm(""); }} disabled={wiping} testId="orders-wipe-cancel">Cancel</Button>
              <button
                onClick={doWipe}
                disabled={wiping || wipeConfirm !== "WIPE ALL ORDERS"}
                data-testid="orders-wipe-confirm-btn"
                className="h-8 px-3 text-[12px] font-bold uppercase tracking-wider bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                <AlertTriangle size={12} /> {wiping ? "Wiping…" : "Delete All"}
              </button>
            </div>
          </div>
        </div>
      )}
      {confirmDeleteSelected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !deletingSelected && setConfirmDeleteSelected(false)} data-testid="orders-delete-selected-modal">
          <div className="bg-white rounded-sm border-2 border-red-500 shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-red-200 bg-red-50 px-4 py-3">
              <AlertTriangle size={18} className="text-red-700" />
              <h2 className="font-heading text-[14px] font-bold text-red-900">Delete Selected Orders</h2>
            </div>
            <div className="p-4 space-y-3 text-[12.5px] text-slate-700">
              <p>This will <b className="text-red-700">permanently delete {selectedIds.size} selected order{selectedIds.size === 1 ? "" : "s"}</b>. This action cannot be undone.</p>
              <label className="block">
                <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Type <span className="font-mono text-red-700 bg-red-50 px-1 rounded">DELETE</span> to confirm</span>
                <input
                  autoFocus
                  value={confirmDeleteText}
                  onChange={(e) => setConfirmDeleteText(e.target.value)}
                  disabled={deletingSelected}
                  data-testid="orders-delete-selected-confirm-input"
                  placeholder="DELETE"
                  className="mt-1 w-full h-8 px-2 text-[12.5px] font-mono border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
              <Button onClick={() => { setConfirmDeleteSelected(false); setConfirmDeleteText(""); }} disabled={deletingSelected} testId="orders-delete-selected-cancel">Cancel</Button>
              <button
                data-testid="orders-delete-selected-confirm-btn"
                disabled={deletingSelected || confirmDeleteText !== "DELETE"}
                onClick={async () => {
                  setDeletingSelected(true);
                  try {
                    const { data } = await api.post("/orders/admin/bulk-delete", {
                      ids: [...selectedIds],
                      confirm: "DELETE",
                    });
                    toast.success(`Deleted ${data.deleted} order${data.deleted === 1 ? "" : "s"}`);
                    setSelectedIds(new Set());
                    setConfirmDeleteSelected(false);
                    setConfirmDeleteText("");
                    load();
                  } catch (e) { toast.error(formatApiError(e)); }
                  finally { setDeletingSelected(false); }
                }}
                className="h-8 px-3 text-[12px] font-bold uppercase tracking-wider bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                <AlertTriangle size={12} /> {deletingSelected ? "Deleting…" : `Delete ${selectedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Feb 27 2026 — Columns picker side panel (dynamic column display). */}
      {showColumnPanel && (
        <ColumnsPanel
          allColumns={/* keep in sync with COLS above */
            [
              { k: "image", label: "Image", group: "Identity" },
              { k: "created_date", label: "Created Date", group: "Identity" },
              { k: "system_id", label: "System ID", group: "Identity" },
              { k: "account_id", label: "Account", group: "Identity" },
              { k: "marketplace", label: "Marketplace", group: "Identity" },
              { k: "marketplace_identifier", label: "Marketplace ID", group: "Identity" },
              { k: "system_sku", label: "System SKU", group: "Identity" },
              { k: "product_name", label: "Product", group: "Identity" },
              { k: "qty_purchased", label: "Qty", group: "Identity" },
              { k: "order_id", label: "Order ID", group: "Identity" },
              { k: "order_item_id", label: "Item ID", group: "Identity" },
              { k: "customer", label: "Customer", group: "Identity" },
              { k: "item_price", label: "Item ₹", group: "Money" },
              { k: "item_tax", label: "Item Tax ₹", group: "Money" },
              { k: "shipping_price", label: "Ship ₹", group: "Money" },
              { k: "final_transfer_price", label: "Final Transfer Price ₹", group: "Money" },
              { k: "china_transfer_price", label: "Transfer ₹", group: "Money" },
              { k: "calculated_sp", label: "Calc SP ₹", group: "Money" },
              { k: "buy_back_price", label: "Buy Back ₹", group: "Money" },
              { k: "china_landing_price", label: "Landing ₹", group: "Money" },
              { k: "china_margin", label: "China Margin ₹", group: "Money" },
              { k: "amazon_commission", label: "Commission ₹", group: "Money" },
              { k: "amazon_payout", label: "Payout ₹", group: "Money" },
              { k: "importrend_margin", label: "Margin ₹", group: "Money" },
              { k: "importrend_status", label: "Importrend", group: "Status" },
              { k: "marketplace_status", label: "Marketplace Status", group: "Status" },
              { k: "china_order_status", label: "China Status", group: "Status" },
              { k: "shipping_status", label: "Shipping", group: "Status" },
              { k: "working_status", label: "Working", group: "Status" },
              { k: "refund_status", label: "Refund", group: "Status" },
              { k: "return_received_status", label: "Return Status", group: "Status" },
              { k: "claim_status", label: "Claim", group: "Claim" },
              { k: "claim_amount", label: "Claim ₹", group: "Claim" },
              { k: "claim_raised_date", label: "Claim Raised", group: "Claim" },
              { k: "shipping_date", label: "Ship Date", group: "Shipping" },
              { k: "courier_name", label: "Courier", group: "Shipping" },
              { k: "tracking_id", label: "Tracking", group: "Shipping" },
              { k: "packaging_video_url", label: "Packaging", group: "Shipping" },
              { k: "china_order_id", label: "China Order ID", group: "China" },
              { k: "china_shipment_id", label: "China Shipment", group: "China" },
              { k: "china_order_date", label: "China Order Date", group: "China" },
              { k: "china_order_dispatched_date", label: "China Dispatched", group: "China" },
              { k: "china_order_received_date", label: "China Received", group: "China" },
              { k: "china_price", label: "China ¥", group: "China" },
              { k: "china_shipping", label: "China Ship ¥", group: "China" },
              { k: "china_weight_gram", label: "Wt (g)", group: "China" },
              { k: "china_qty_to_order", label: "China Qty", group: "China" },
              { k: "variant", label: "Variant", group: "China" },
              { k: "source_url", label: "Source URL", group: "China" },
              { k: "purchase_date", label: "Purchase Date", group: "Customer" },
              { k: "buyer_email", label: "Buyer Email", group: "Customer" },
              { k: "buyer_phone_no", label: "Buyer Phone", group: "Customer" },
              { k: "recipient_name", label: "Recipient", group: "Customer" },
              { k: "ship_city", label: "Ship City", group: "Customer" },
              { k: "ship_state", label: "Ship State", group: "Customer" },
              { k: "ship_postal_code", label: "Pincode", group: "Customer" },
              { k: "payment_method", label: "Payment", group: "Customer" },
              { k: "is_prime", label: "Prime", group: "Customer" },
              { k: "inventory_allocated", label: "Allocated", group: "Classification" },
              { k: "gst_rate", label: "GST %", group: "Classification" },
              { k: "electrical_type", label: "Electrical", group: "Classification" },
              { k: "hsn", label: "HSN", group: "Classification" },
              { k: "tags", label: "Tags", group: "Classification" },
              { k: "comments", label: "Comments", group: "Classification" },
            ]}
          initial={columnPref}
          onClose={() => setShowColumnPanel(false)}
          onSave={async (next) => {
            try {
              const { data } = await api.patch("/auth/preferences/orders_columns", { value: next });
              setColumnPref(data.value || {});
              toast.success("Column layout saved");
              setShowColumnPanel(false);
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          onReset={async () => {
            try {
              await api.patch("/auth/preferences/orders_columns", { value: null });
              setColumnPref({});
              toast.success("Reset to default column layout");
              setShowColumnPanel(false);
            } catch (e) { toast.error(formatApiError(e)); }
          }}
        />
      )}
      {/* Feb 27 2026 — Bulk Update confirm modal */}
      {bulkConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
             onClick={() => !bulkRunning && setBulkConfirm(null)}
             data-testid="bulk-confirm-modal">
          <div className="bg-white rounded-sm border-2 border-blue-500 shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-blue-200 bg-blue-50 px-4 py-3">
              <History size={18} className="text-blue-700" />
              <h2 className="font-heading text-[14px] font-bold text-blue-900">Confirm Bulk Update</h2>
            </div>
            <div className="p-4 space-y-3 text-[12.5px] text-slate-700">
              {bulkConfirm.count === 0 ? (
                <p className="text-red-700 font-semibold" data-testid="bulk-confirm-none">
                  No orders match your current filter{bulkConfirm.mode === "blanks" ? " (with a blank value in this field)" : ""}. Nothing to update.
                </p>
              ) : (
                <>
                  {!bulkHasFilterActive && (
                    <div className="text-red-700 font-bold bg-red-50 border border-red-200 rounded-sm px-2 py-1.5">
                      ⚠ No filter is active — this will update <b>ALL orders</b> in the system.
                    </div>
                  )}
                  <p data-testid="bulk-confirm-count">
                    Update <b>{BULK_FIELD_LABELS[bulkField]}</b> to <b className="font-mono text-blue-700">{String(bulkValue) || "(blank)"}</b> for <b className="text-blue-700">{bulkConfirm.count}</b> order{bulkConfirm.count === 1 ? "" : "s"}?
                    {bulkConfirm.scope === "selection" && (
                      <span className="block mt-1 text-[11.5px] text-emerald-700 italic" data-testid="bulk-confirm-scope-selection">Scope: only the rows you have ticked ({bulkConfirm.count} of {selectedIds.size} selected).</span>
                    )}
                    {bulkConfirm.scope === "filter" && (
                      <span className="block mt-1 text-[11.5px] text-slate-500 italic" data-testid="bulk-confirm-scope-filter">Scope: every order matching the current filter / search.</span>
                    )}
                    {bulkConfirm.mode === "blanks" && (
                      <span className="block mt-1 text-[11.5px] text-slate-500 italic">Only orders where this field is currently blank will be updated.</span>
                    )}
                  </p>
                </>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3 bg-slate-50">
              <Button onClick={() => setBulkConfirm(null)} disabled={bulkRunning} testId="bulk-confirm-cancel">Cancel</Button>
              <button
                data-testid="bulk-confirm-apply"
                disabled={bulkRunning || bulkConfirm.count === 0}
                onClick={runBulkApply}
                className="h-8 px-3 text-[12px] font-bold uppercase tracking-wider bg-blue-600 text-white rounded-sm hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
              >
                <History size={12} /> {bulkRunning ? "Updating…" : `Update ${bulkConfirm.count}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
